import React, { useState, useEffect, createContext, useContext, useMemo, useRef, useCallback } from 'react';
import { CONFIG, getRequiredConfig } from './config';
import {
  AppBar, Avatar, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, createTheme, CssBaseline, Dialog, DialogContent, DialogTitle, Divider, Drawer, FormControl, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, TextField, ThemeProvider, Toolbar, Tooltip, Typography, Switch, ToggleButton, ToggleButtonGroup, Slider
} from "@mui/material";

// --- ICONS ---
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
  Extension as ExtensionIcon,
  MoreVert as MoreVertIcon,
  PhotoCamera as PhotoCameraIcon,
  Send as SendIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";

import { Select, FormControl as MuiFormControl, InputLabel, DialogActions, Skeleton } from "@mui/material";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  FormLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// --- Utilities ---
// Robust timestamp parser to handle ISO, BigQuery-like and naive formats
function parseMessageTimestamp(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    const s = String(ts).trim();
    // Fast path: native parser works
    let d = Date.parse(s);
    if (!Number.isNaN(d)) return d;
    // Normalize 'YYYY-MM-DD HH:MM:SS' to ISO and append Z if missing timezone
    let norm = s.replace(' ', 'T');
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(norm)) norm += 'Z';
    d = Date.parse(norm);
    if (!Number.isNaN(d)) return d;
    // Try to fix timezone without colon (e.g., +0000 -> +00:00)
    norm = norm.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    d = Date.parse(norm);
    return Number.isNaN(d) ? 0 : d;
  } catch (_) {
    return 0;
  }
}

const resolveApiBaseUrl = () => {
  const envBase = (process.env.REACT_APP_API_BASE_URL || CONFIG.API_BASE_URL || '').trim();
  if (!envBase) {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return '';
  }
  if (/^https?:\/\//i.test(envBase)) {
    return envBase.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const prefix = envBase.startsWith('/') ? '' : '/';
    return `${window.location.origin}${prefix}${envBase}`.replace(/\/$/, '');
  }
  return envBase.replace(/\/$/, '');
};

const API_BASE_URL = resolveApiBaseUrl();
const POLARIS_ICON_URL = getRequiredConfig(CONFIG.POLARIS_ICON_URL, 'REACT_APP_POLARIS_ICON_URL');
const resolvePolarisBaseUrl = () => {
  const explicit =
    (process.env.REACT_APP_POLARIS_API_BASE_URL || '').trim() ||
    (CONFIG.POLARIS_APP_USER_ENDPOINT || '').trim() ||
    (process.env.REACT_APP_POLARIS_APP_USER_ENDPOINT || '').trim();
  if (!explicit) return API_BASE_URL || '';
  if (/^https?:\/\//i.test(explicit)) {
    try {
      return new URL(explicit).origin;
    } catch (_) {
      return explicit.replace(/\/$/, '');
    }
  }
  if (typeof window !== 'undefined') {
    const prefix = explicit.startsWith('/') ? '' : '/';
    return `${window.location.origin}${prefix}${explicit}`.replace(/\/$/, '');
  }
  return explicit.replace(/\/$/, '');
};
const POLARIS_API_BASE_URL = resolvePolarisBaseUrl();
const buildApiUrl = (path = '') => {
  if (!path) {
    return POLARIS_API_BASE_URL || '';
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${POLARIS_API_BASE_URL}${normalized}`;
};

const defaultChatKitOptions = {
  api: {},
  theme: {
    colorScheme: 'light',
    radius: 'sharp',
    density: 'normal',
    color: {
      accent: {
        primary: 'rgb(15, 17, 22)',
        level: 1,
      },
    },
    typography: {
      baseSize: 16,
      fontFamily:
        '"OpenAI Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      fontFamilyMono:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "DejaVu Sans Mono", "Courier New", monospace',
      fontSources: [
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Regular.woff2',
          weight: 400,
          style: 'normal',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Medium.woff2',
          weight: 500,
          style: 'normal',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-SemiBold.woff2',
          weight: 600,
          style: 'normal',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Bold.woff2',
          weight: 700,
          style: 'normal',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Italic.woff2',
          weight: 400,
          style: 'italic',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-MediumItalic.woff2',
          weight: 500,
          style: 'italic',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-SemiBoldItalic.woff2',
          weight: 600,
          style: 'italic',
          display: 'swap',
        },
        {
          family: 'OpenAI Sans',
          src: 'https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-BoldItalic.woff2',
          weight: 700,
          style: 'italic',
          display: 'swap',
        },
      ],
    },
  },
  composer: {
    placeholder: 'Type a message…',
    attachments: {
      enabled: true,
      maxCount: 6,
      maxSize: 10 * 1024 * 1024,
      accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] },
    },
    tools: [
      {
        id: 'sendEscalationRequestForm',
        label: 'IT Support',
        shortLabel: 'IT',
        placeholderOverride: 'Describe the support issue…',
        icon: 'headset',
        pinned: false,
      },
      {
        id: 'isPlanCommissionable',
        label: 'Commissionable Plan',
        shortLabel: 'Commission',
        placeholderOverride: 'Check if a plan is commissionable…',
        icon: 'badge-percent',
        pinned: false,
      },
      {
        id: 'sendElectionPeriodRequest',
        label: 'SEP Request',
        shortLabel: 'SEP',
        placeholderOverride: 'Collect SEP qualifier details…',
        icon: 'calendar-plus',
        pinned: false,
      },
      {
        id: 'triggerSPAPCard',
        label: 'SPAP Card',
        shortLabel: 'SPAP',
        placeholderOverride: 'Generate an SPAP card…',
        icon: 'id-card',
        pinned: false,
      },
      {
        id: 'triggerPlanResearch',
        label: 'Plan Research',
        shortLabel: 'Plans',
        placeholderOverride: 'Search for plan details…',
        icon: 'search',
        pinned: false,
      },
      {
        id: 'triggerProviderNetworkResearch',
        label: 'Provider Lookup',
        shortLabel: 'Provider',
        placeholderOverride: 'Find provider network info…',
        icon: 'stethoscope',
        pinned: false,
      },
      {
        id: 'triggerRXResearch',
        label: 'Medication Lookup',
        shortLabel: 'RX',
        placeholderOverride: 'Review medication coverage…',
        icon: 'capsules',
        pinned: false,
      },
      {
        id: 'SET_REMINDER',
        label: 'Set Reminder',
        shortLabel: 'Reminder',
        placeholderOverride: 'Schedule a reminder…',
        icon: 'bell',
        pinned: false,
      },
    ],
  },
  startScreen: {
    greeting: '',
    prompts: [
      { icon: 'book-open', label: 'Plan research', prompt: 'What is tricare?' },
      { icon: 'user-doctor', label: 'Provider lookup', prompt: 'Check if Dr. Rivera in Tampa, FL accepts Humana.' },
      { icon: 'calendar', label: 'Schedule reminder', prompt: 'How much is the food benefit on the Humana Gold Plus H1036-210 (HMO D-SNP) plan?' },
    ],
  },
  header: {
    customButtonRight: { icon: 'settings-cog', tooltip: 'Chat appearance' },
  },
  history: { enabled: false },
};

const normalizeColorString = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim().toLowerCase();
  if (trimmed.startsWith('rgba') || trimmed.startsWith('rgb')) {
    const match = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const [r, g, b] = match.slice(1, 4).map((num) => {
        const parsed = parseInt(num, 10);
        if (Number.isNaN(parsed)) return 0;
        return Math.max(0, Math.min(255, parsed));
      });
      return `#${[r, g, b].map((component) => component.toString(16).padStart(2, '0')).join('')}`;
    }
  }
  if (trimmed.startsWith('#')) {
    if (trimmed.length === 4) {
      const r = trimmed[1];
      const g = trimmed[2];
      const b = trimmed[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (trimmed.length === 7) {
      return trimmed;
    }
  }
  return trimmed.replace(/\s+/g, '');
};

const mergeDeep = (target, source) => {
  const output = { ...(target || {}) };
  if (!source || typeof source !== 'object') {
    return output;
  }
  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = output[key];
    if (Array.isArray(sourceValue)) {
      output[key] = sourceValue.slice();
    } else if (sourceValue && typeof sourceValue === 'object') {
      output[key] = mergeDeep(targetValue && typeof targetValue === 'object' ? targetValue : {}, sourceValue);
    } else if (sourceValue !== undefined) {
      output[key] = sourceValue;
    }
  });
  return output;
};

const sanitizeChatkitOptions = (options) => {
  const merged = mergeDeep(defaultChatKitOptions, options && typeof options === 'object' ? options : {});
  const sanitized = mergeDeep({}, merged);

  if (sanitized?.theme?.typography) {
    sanitized.theme.typography = mergeDeep({}, defaultChatKitOptions.theme.typography);
  }

  sanitized.startScreen = mergeDeep({}, defaultChatKitOptions.startScreen);

  const composerDefaults = mergeDeep({}, defaultChatKitOptions.composer);
  const composer = mergeDeep(composerDefaults, sanitized.composer || {});
  composer.placeholder = defaultChatKitOptions.composer.placeholder;
  sanitized.composer = composer;

  return sanitized;
};

const ChatKitThemeContext = createContext(null);

const useChatKitTheme = () => useContext(ChatKitThemeContext);

const ChatKitThemeProvider = ({ children }) => {
  const [chatkitOptions, setChatkitOptions] = useState(() => {
    if (typeof window === 'undefined') {
      return sanitizeChatkitOptions(defaultChatKitOptions);
    }
    try {
      const stored = window.localStorage.getItem('chatkit_theme_options');
      if (stored) {
        return sanitizeChatkitOptions(JSON.parse(stored));
      }
    } catch (err) {
      console.warn('Failed to load ChatKit options from storage', err);
    }
    return sanitizeChatkitOptions(defaultChatKitOptions);
  });

  const updateChatkitOptions = useCallback((updater) => {
    setChatkitOptions((prev) => {
      const base = mergeDeep({}, prev);
      const next = typeof updater === 'function' ? updater(base) : mergeDeep(base, updater);
      return sanitizeChatkitOptions(next || {});
    });
  }, []);

  const resetChatkitOptions = useCallback(() => {
    setChatkitOptions(() => sanitizeChatkitOptions(defaultChatKitOptions));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('chatkit_theme_options', JSON.stringify(chatkitOptions));
    } catch (err) {
      console.warn('Failed to persist ChatKit options', err);
    }
  }, [chatkitOptions]);

  const value = useMemo(
    () => ({
      chatkitOptions,
      updateChatkitOptions,
      resetChatkitOptions,
    }),
    [chatkitOptions, updateChatkitOptions, resetChatkitOptions],
  );

  return <ChatKitThemeContext.Provider value={value}>{children}</ChatKitThemeContext.Provider>;
};


// --- AUTHENTICATION CONTEXT ---
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        if (typeof window === 'undefined') {
          setUser(null);
          setAppUser(null);
          return;
        }

        const params = new URLSearchParams(window.location.search || '');
        const normalizeEmail = (value) => {
          if (!value) return null;
          const trimmed = String(value).trim().toLowerCase();
          return trimmed || null;
        };
        const normalizeString = (value, { toLower = false } = {}) => {
          if (!value) return null;
          const trimmed = String(value).trim();
          if (!trimmed) return null;
          return toLower ? trimmed.toLowerCase() : trimmed;
        };

        const emailCandidates = [
          params.get('userEmail'),
          params.get('email'),
          params.get('e'),
          params.get('u'),
          window.localStorage.getItem('activeUserEmail'),
          window.localStorage.getItem('userEmail'),
          window.localStorage.getItem('devUserEmail'),
          process.env.REACT_APP_DEV_USER_EMAIL,
        ];

        const email = emailCandidates.map(normalizeEmail).find(Boolean) || null;
        const threadIdCandidates = [
          params.get('threadId'),
          window.localStorage.getItem('polarisThreadId'),
        ];
        const userIdCandidates = [
          params.get('userId'),
          window.localStorage.getItem('polarisUserId'),
        ];
        const statusCandidates = [
          params.get('userStatus'),
          window.localStorage.getItem('polarisUserStatus'),
        ];
        const preloadedThreadId = threadIdCandidates.map((value) => normalizeString(value)).find(Boolean) || null;
        const preloadedUserId = userIdCandidates.map((value) => normalizeString(value)).find(Boolean) || null;
        const preloadedStatus = statusCandidates.map((value) => normalizeString(value, { toLower: true })).find(Boolean) || null;

        const resolveName = () => {
          const candidates = [
            params.get('userName'),
            params.get('name'),
            window.localStorage.getItem('activeUserName'),
            window.localStorage.getItem('userName'),
          ]
            .map((value) => {
              if (!value) return null;
              const trimmed = String(value).trim();
              return trimmed || null;
            })
            .filter(Boolean);

          if (candidates.length > 0) return candidates[0];
          if (email) return email.split('@')[0];
          return null;
        };

        const name = resolveName();

        if (!email) {
          setUser(null);
          setAppUser(null);
          return;
        }

        const polarisUser = { email, name: name || '', id: null };
        setUser(polarisUser);

        const hasPreloadedProfile = Boolean(preloadedThreadId || preloadedUserId);
        if (hasPreloadedProfile) {
          const threadIds = preloadedThreadId ? [preloadedThreadId] : [];
          const status = preloadedStatus || 'active';
          setAppUser({
            userId: preloadedUserId,
            status,
            threadId: preloadedThreadId,
            threadIds,
            raw: {
              user_id: preloadedUserId,
              userId: preloadedUserId,
              thread_id: preloadedThreadId,
              threadId: preloadedThreadId,
              user_status: status,
            },
          });
          if (typeof window !== 'undefined') {
            try {
              if (preloadedUserId) window.localStorage.setItem('polarisUserId', preloadedUserId);
              if (preloadedThreadId) window.localStorage.setItem('polarisThreadId', preloadedThreadId);
              if (status) window.localStorage.setItem('polarisUserStatus', status);
            } catch (_) {}
          }
        }

        try {
          if (!preloadedThreadId) {
            const resp = await fetch(buildApiUrl('/app-user'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userEmail: email, user_full_name: name || email.split('@')[0] }),
            });

            if (resp.ok) {
              const data = await resp.json();
              const appUserId = data.userId || data.user_id || null;
              const userStatus = data.user_status || 'active';
              const threadId = data.thread_id
                || data.threadId
                || (Array.isArray(data.thread_ids) && data.thread_ids.length ? data.thread_ids[0] : null)
                || null;
              const threadIds = threadId ? [threadId] : (Array.isArray(data.thread_ids) ? data.thread_ids : []);
              setAppUser({ userId: appUserId, status: userStatus, threadId, threadIds, raw: data });
              if (typeof window !== 'undefined') {
                try {
                  if (appUserId) localStorage.setItem('polarisUserId', appUserId);
                  if (threadId) localStorage.setItem('polarisThreadId', threadId);
                  if (userStatus) localStorage.setItem('polarisUserStatus', String(userStatus).toLowerCase());
                } catch (_) {}
              }
            if (String(userStatus).toLowerCase() !== 'active') {
              console.warn(`Polaris account status: ${userStatus}`);
            }
          } else {
            console.warn(`Polaris user validation failed (${resp.status})`);
            if (!hasPreloadedProfile) setAppUser(null);
          }
        }
      } catch (validationError) {
        console.error('Failed to validate Polaris user:', validationError);
        if (!hasPreloadedProfile) setAppUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value = useMemo(
    () => ({
      user,
      appUser,
      loading,
    }),
    [user, appUser, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
const WidgetImage = ({
  src,
  alt,
  frame = false,
  fit = 'cover',
  radius = 3,
  maxWidth = '100%',
  maxHeight,
}) => {
  if (!src) return null;
  const image = (
    <Box
      component="img"
      src={src}
      alt={alt || ''}
      sx={{
        width: '100%',
        maxWidth,
        maxHeight,
        display: 'block',
        objectFit: fit,
        borderRadius: radius === 'full' ? 9999 : radius,
      }}
    />
  );
  if (frame) {
    return (
      <Paper elevation={1} sx={{ p: 1, borderRadius: radius === 'full' ? 9999 : radius, mb: 1, maxWidth }}>
        {image}
      </Paper>
    );
  }
  return (
    <Box sx={{ mb: 1, maxWidth }}>
      {image}
    </Box>
  );
};

const WidgetCard = ({ title, caption, body = [], context }) => {
  const theme = useTheme();
  const bg = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.background.paper;
  const textColor = theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.text.primary;
  const captionColor = theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.text.secondary;
  return (
    <Paper
      elevation={1}
      className="chat-inline-card"
      sx={{
        p: 2,
        borderRadius: 3,
        mb: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        bgcolor: bg,
        color: textColor,
      }}
    >
      {caption && (
        <Typography variant="overline" sx={{ color: captionColor }}>
          {caption}
        </Typography>
      )}
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 600, color: textColor }}>
          {title}
        </Typography>
      )}
      {Array.isArray(body) && body.map((line, idx) => (
        <Typography key={idx} variant="body2" sx={{ color: textColor }}>
          {line}
        </Typography>
      ))}
      {context && (
        <Typography variant="caption" sx={{ color: captionColor }}>
          {context}
        </Typography>
      )}
    </Paper>
  );
};

function ChatMessage({ message, meEmail, meName, onAction, threadId, remoteThreadId, userAccentColor }) {
  const isMobile = (typeof window !== 'undefined') && window.matchMedia && window.matchMedia('(max-width:600px)').matches;
  const isMe = message.sender === meEmail;
  const [formState, setFormState] = React.useState({});
  const [actionLoading, setActionLoading] = React.useState(false);
  const isBot = String(message?.sender || '').toLowerCase() === 'polaris-bot';
  const polarisLogo = POLARIS_ICON_URL;
  const userInitial = (meName || (meEmail || '').split('@')[0] || 'U').charAt(0).toUpperCase();
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxSrc, setLightboxSrc] = React.useState('');
  const onImgClick = React.useCallback((src) => {
    if (!src) return;
    setLightboxSrc(src);
    setLightboxOpen(true);
  }, [setLightboxOpen, setLightboxSrc]);
  const theme = useTheme();
  const effectiveUserAccent = userAccentColor || theme.palette.primary.main;
  const userContrastText = theme.palette.getContrastText
    ? theme.palette.getContrastText(effectiveUserAccent)
    : theme.palette.primary.contrastText;
  const bubbleBg = isMe
    ? effectiveUserAccent
    : (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[200]);
  const bubbleTextColor = isMe
    ? userContrastText
    : (theme.palette.mode === 'dark' ? theme.palette.grey[50] : theme.palette.text.primary);
  const bubbleMetaColor = isMe
    ? userContrastText
    : (theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.text.secondary);
  const loaderSkeletonLight = isMe ? 'rgba(255,255,255,0.25)' : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)');
  const loaderSkeletonLight2 = isMe ? 'rgba(255,255,255,0.22)' : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)');
  const cardBg = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.background.paper;
  const cardTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.text.primary;

  const isImageHttpUrl = (s) => {
    if (typeof s !== 'string') return false;
    const u = s.trim();
    if (!/^https?:\/\//i.test(u)) return false;
    // Accept common image extensions; allow query strings
    return /(\.png|\.jpe?g|\.gif|\.webp|\.bmp|\.svg)(\?.*)?$/i.test(u);
  };
  const isDataImage = (s) => typeof s === 'string' && /^data:image\//i.test(s.trim());

  // Extract images referenced in message.text via [Attachments] blocks and remove raw URLs from text
  const extractImagesFromText = (text) => {
    if (!text || typeof text !== 'string') return { strippedText: text || '', images: [] };
    const lines = text.split(/\r?\n/);
    const images = [];
    const kept = [];
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const line = raw.trim();
      if (line === '[Attachments]') {
        // Consume subsequent lines that look like image URLs until next header or a non-URL content line
        i++;
        while (i < lines.length) {
          const candRaw = lines[i];
          const cand = (candRaw || '').trim();
          if (!cand) { i++; continue; }
          if (cand === '[Attachments]') { i--; break; }
          if (isDataImage(cand) || isImageHttpUrl(cand)) {
            images.push(cand);
            i++;
            continue;
          }
          // Stop if we hit non-URL content
          i--; // step back so outer loop processes this line
          break;
        }
      } else {
        kept.push(raw);
      }
    }

    // Also catch any stray data:image URIs in the remaining text (remove and extract)
    let joined = kept.join('\n');
    const dataUriRegex = /data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g;
    const strayDataUris = joined.match(dataUriRegex) || [];
    if (strayDataUris.length) {
      images.push(...strayDataUris);
      joined = joined.replace(dataUriRegex, '').replace(/\n{3,}/g, '\n\n');
    }

    // Extract markdown image syntax: ![alt](url)
    try {
      const mdImgRegex = /!\[[^\]]*]\(([^)]+)\)/g;
      let m;
      while ((m = mdImgRegex.exec(joined)) !== null) {
        const url = (m[1] || '').trim();
        if (isDataImage(url) || isImageHttpUrl(url)) {
          images.push(url);
        }
      }
      joined = joined.replace(mdImgRegex, '').replace(/\n{3,}/g, '\n\n');
    } catch (_) {}

    // Extract bare http(s) image URLs present inline and remove them from text
    try {
      const httpImgRegex = /(https?:\/\/[^\s)>'"<>]+?\.(?:png|jpe?g|gif|webp|bmp|svg)(?:\?[^\s)'"<>]*)?)/ig;
      let m2;
      while ((m2 = httpImgRegex.exec(joined)) !== null) {
        const url = (m2[1] || '').trim();
        if (isImageHttpUrl(url)) images.push(url);
      }
      joined = joined.replace(httpImgRegex, '').replace(/\n{3,}/g, '\n\n');
    } catch (_) {}

    // Extract images from base64 JSON (OpenAI image generation response shape)
    try {
      const fmtFrom = (s) => {
        const m = String(s || '').match(/"output_format"\s*:\s*"(png|jpeg|webp)"/i);
        return (m && m[1]) ? m[1].toLowerCase() : 'png';
      };
      const tryParseFull = (s) => {
        try {
          const obj = JSON.parse(s);
          if (obj && Array.isArray(obj.data)) {
            const fmt = (obj.output_format || 'png').toLowerCase();
            obj.data.forEach(it => {
              const b64 = it && (it.b64_json || it.base64 || it.b64);
              if (b64 && typeof b64 === 'string') {
                images.push(`data:image/${fmt};base64,${b64}`);
              }
            });
            return true;
          }
        } catch (_) {}
        return false;
      };
      const trimmedJson = joined.trim();
      let consumed = false;
      if (trimmedJson.startsWith('{') && trimmedJson.endsWith('}')) {
        consumed = tryParseFull(trimmedJson);
        if (consumed) joined = '';
      }
      if (!consumed && /"b64_json"\s*:\s*"/.test(joined)) {
        const fmt = fmtFrom(joined);
        const re = /"b64_json"\s*:\s*"([^"]+)"/g;
        let m3;
        while ((m3 = re.exec(joined)) !== null) {
          const b64 = (m3[1] || '').trim();
          if (b64) images.push(`data:image/${fmt};base64,${b64}`);
        }
        // Strip b64_json occurrences from shown text
        joined = joined.replace(/"b64_json"\s*:\s*"[^"]+"/g, '').replace(/\n{3,}/g, '\n\n');
      }
    } catch (_) {}

    // Normalize: prefer data:image when both data and http(s) exist in the same message
    const hasData = images.some(isDataImage);
    let deduped = [];
    if (hasData) {
      deduped = Array.from(new Set(images.filter(isDataImage)));
    } else {
      deduped = Array.from(new Set(images));
    }

    return { strippedText: joined.trim(), images: deduped };
  };

  const resolveParamValue = (tpl) => {
    if (typeof tpl !== 'string') return tpl;
    return tpl.replace(/\$\{([^}]+)\}/g, (_, k) => (formState[k] ?? ''));
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      const fn = action?.function;
      if (!fn) return;
      const paramsList = action?.parameters || [];
      const params = {};
      paramsList.forEach(p => {
        if (p && p.key)
          params[p.key] = resolveParamValue(p.value ?? '');
      });
      // Build simple formInputs map from current form state
      const formInputs = {};
      Object.entries(formState).forEach(([k, v]) => {
        formInputs[k] = String(Array.isArray(v) ? v.join(',') : v ?? '');
      });
      const localThreadId = threadId || '';
      const remoteThreadIdentifier = typeof remoteThreadId === 'string' && remoteThreadId.trim()
        ? remoteThreadId.trim()
        : '';
      const resp = await fetch(buildApiUrl('/webkit/card-action'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': meEmail },
        body: JSON.stringify({
          function: fn,
          email: meEmail,
          display_name: meName || (meEmail || '').split('@')[0],
          parameters: params,
          formInputs,
          thread_id: localThreadId || undefined,
          local_thread_id: localThreadId || undefined,
          assistant_thread_id: remoteThreadIdentifier || undefined,
          createTime: new Date().toISOString(),
        })
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${t}`);
      }
      const ct = (resp.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('application/json')) {
        const data = await resp.json();
        if (onAction) onAction({ ...data, _toolKey: fn });
      } else {
        const text = await resp.text();
        if (onAction) onAction({ assistant_text: String(text || '').trim(), _toolKey: fn });
      }
    } catch (e) {
      console.error('Card action failed:', e);
    } finally { setActionLoading(false); }
  };
  // Parse any embedded images from message text
  const parsed = extractImagesFromText(message.text);
  let renderedText = parsed.strippedText;
  const widgetTextFilters = [];
  const explicitAttachments = (message.attachments || []).map(a => a?.dataUrl || a?.url).filter(Boolean);
  const widgets = Array.isArray(message.widgets) ? message.widgets : [];
  const allowInlineImages = !widgets.some((w) => w?.type === 'image');
  const allImgsRaw = allowInlineImages ? [...explicitAttachments, ...parsed.images] : [];
  const hasDataInAll = allImgsRaw.some(isDataImage);
  const allImgs = hasDataInAll
    ? Array.from(new Set(allImgsRaw.filter(isDataImage)))
    : Array.from(new Set(allImgsRaw));

  const renderedWidgets = widgets.flatMap((widget, idx) => {
    if (!widget) return null;
    if (widget.type === 'image') {
      return (
        <WidgetImage
          key={`widget-image-${idx}`}
          src={widget.src}
          alt={widget.alt}
          frame={widget.frame}
          fit={widget.fit}
          radius={widget.radius}
          maxWidth={widget.maxWidth || widget.size || '100%'}
          maxHeight={widget.maxHeight}
        />
      );
    }
    if (widget.type === 'card') {
      return widget.card?.sections?.map((section, sectionIdx) => {
        const header = section.header || {};
        const rows = [];
        if (header.title || header.subtitle || header.imageUrl) {
          rows.push(
            <Box key={`card-header-${idx}-${sectionIdx}`} sx={{ mb: 1 }}>
              {header.title && <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{header.title}</Typography>}
              {header.subtitle && <Typography variant="body2" color="text.secondary">{header.subtitle}</Typography>}
            </Box>
          );
        }
        (section.widgets || []).forEach((w, widgetIdx) => {
          if (w.keyValue) {
            rows.push(
              <Box key={`card-kv-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, pr: 1 }}>{w.keyValue.topLabel || ''}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'right', flex: 1 }}>{w.keyValue.content || ''}</Typography>
              </Box>
            );
          }
          if (w.textParagraph) {
            rows.push(
              <Typography key={`card-text-${idx}-${sectionIdx}-${widgetIdx}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{w.textParagraph.text}</Typography>
            );
          }
          if (w.image) {
            rows.push(
              <Box key={`card-img-${idx}-${sectionIdx}-${widgetIdx}`} component="img" src={w.image.imageUrl} alt="" sx={{ maxWidth: '100%', borderRadius: 1 }} />
            );
          }
          if (w.decoratedText) {
            const buttonConfig = w.decoratedText?.button || {};
            const btn = buttonConfig?.onClick?.action;
            const buttonLabel = (buttonConfig?.text || '').trim() || 'Select';
            const textValue = (w.decoratedText?.text || '').trim();
            const showText = Boolean(textValue && textValue !== buttonLabel);
            if (textValue) widgetTextFilters.push(textValue);
            if (buttonLabel) widgetTextFilters.push(buttonLabel);
            rows.push(
              <Box key={`card-decorated-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {showText && (
                  <Typography variant="body2" sx={{ flex: 1 }}>{textValue}</Typography>
                )}
                {btn && (
                  <Button size="small" variant="outlined" onClick={() => handleAction(btn)} disabled={actionLoading}>
                    {buttonLabel}
                  </Button>
                )}
              </Box>
            );
          }
          if (w.selectionInput) {
            const siw = w.selectionInput;
            const name = siw.name || `card_sel_${idx}_${sectionIdx}_${widgetIdx}`;
            const selected = formState[name] ?? (siw.items?.find(i => i.selected)?.value || '');
            if ((siw.type || '').toUpperCase() === 'DROPDOWN') {
              rows.push(
                <Box key={`card-select-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ my: 0.5 }}>
                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                  <Select size="small" fullWidth value={selected} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} disabled={actionLoading}>
                    {(siw.items || []).map((item, optIdx) => (
                      <MenuItem key={optIdx} value={item.value || item.text}>{item.text || item.value}</MenuItem>
                    ))}
                  </Select>
                </Box>
              );
            }
            if ((siw.type || '').toUpperCase() === 'RADIO_BUTTON') {
              rows.push(
                <Box key={`card-radio-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ my: 0.5 }}>
                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                  <RadioGroup row value={selected} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))}>
                    {(siw.items || []).map((item, optIdx) => (
                      <FormControlLabel key={optIdx} value={item.value || item.text} control={<Radio size="small" disabled={actionLoading} />} label={item.text || item.value} />
                    ))}
                  </RadioGroup>
                </Box>
              );
            }
            if ((siw.type || '').toUpperCase() === 'CHECK_BOX') {
              const values = Array.isArray(formState[name]) ? formState[name] : [];
              const toggle = (val) => setFormState(s => {
                const arr = new Set(Array.isArray(s[name]) ? s[name] : []);
                if (arr.has(val)) arr.delete(val); else arr.add(val);
                return { ...s, [name]: Array.from(arr) };
              });
              rows.push(
                <Box key={`card-checkbox-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ my: 0.5 }}>
                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                  <FormGroup row>
                    {(siw.items || []).map((item, optIdx) => (
                      <FormControlLabel key={optIdx} control={<Checkbox size="small" checked={values.includes(item.value || item.text)} onChange={() => toggle(item.value || item.text)} disabled={actionLoading} />} label={item.text || item.value} />
                    ))}
                  </FormGroup>
                </Box>
              );
            }
          }
          if (w.textInput) {
            const ti = w.textInput;
            const name = ti.name || `card_txt_${idx}_${sectionIdx}_${widgetIdx}`;
            const val = formState[name] ?? '';
            const multiline = (ti.type || '').toUpperCase() === 'MULTIPLE_LINE';
            rows.push(
              <Box key={`card-textinput-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ my: 0.5 }}>
                {ti.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{ti.label}</Typography>)}
                <TextField fullWidth size="small" value={val} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} multiline={multiline} minRows={multiline ? 3 : 1} disabled={actionLoading} />
              </Box>
            );
          }
          if (w.dateTimePicker) {
            const dt = w.dateTimePicker;
            const name = dt.name || `card_dt_${idx}_${sectionIdx}_${widgetIdx}`;
            const val = formState[name] ?? '';
            const type = (dt.type || '').toUpperCase() === 'DATE_AND_TIME' ? 'datetime-local' : 'date';
            rows.push(
              <Box key={`card-datetime-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ my: 0.5 }}>
                {dt.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{dt.label}</Typography>)}
                <TextField type={type} size="small" value={val} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} disabled={actionLoading} />
              </Box>
            );
          }
          if (w.buttonList) {
            const bl = w.buttonList;
            rows.push(
              <Box key={`card-buttons-${idx}-${sectionIdx}-${widgetIdx}`} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {(bl.buttons || []).map((b, bi) => {
                  if (b?.text) widgetTextFilters.push(String(b.text));
                  const act = b?.onClick?.action;
                  return (
                    <Button key={bi} size="small" variant="contained" onClick={() => act && handleAction(act)} disabled={actionLoading}>
                      {b.text || 'Submit'}
                    </Button>
                  );
                })}
              </Box>
            );
          }
        });
        return rows.length ? <Box key={`card-section-${idx}-${sectionIdx}`} sx={{ mb: 1 }}>{rows}</Box> : null;
      });
    }
    return null;
  });

  if (widgetTextFilters.length && typeof renderedText === 'string' && renderedText) {
    const normalizedFilters = new Set(
      widgetTextFilters
        .map((s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase())
        .filter(Boolean)
    );
    renderedText = renderedText
      .split('\n')
      .filter((line) => {
        const normalized = String(line || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (!normalized) return true;
        return !normalizedFilters.has(normalized);
      })
      .join('\n');
  }

  const finalRenderedText = typeof renderedText === 'string'
    ? renderedText.replace(/\n{3,}/g, '\n\n').trim()
    : '';
  const showRenderedText = Boolean(finalRenderedText);
  const loaderText = message.loading
    ? (finalRenderedText || (typeof message.text === 'string' ? message.text.trim() : ''))
    : '';
  const showLoaderText = Boolean(loaderText);
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 1, alignItems: 'flex-end', gap: 1 }}>
        {!isMe && (
          <Avatar src={isBot ? polarisLogo : undefined} sx={{ width: 28, height: 28, bgcolor: isBot ? 'transparent' : 'primary.main', color: isBot ? 'inherit' : 'primary.contrastText' }}>
            {!isBot ? (message.senderName?.charAt(0)?.toUpperCase() || 'U') : null}
          </Avatar>
        )}
        <Box
          sx={{
            maxWidth: isMobile ? '90vw' : '70%',
            px: 2,
            py: 1.25,
            borderRadius: 2,
            bgcolor: bubbleBg,
            color: bubbleTextColor,
            boxShadow: 1,
            whiteSpace: 'pre-wrap'
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.25, color: bubbleMetaColor }}>
            {isMe ? 'You' : (message.senderName || message.sender)}
          </Typography>
          {message.loading ? (
            showLoaderText ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: bubbleTextColor }} />
                <Typography
                  variant="body1"
                  sx={{ color: bubbleTextColor, whiteSpace: 'pre-wrap' }}
                >
                  {loaderText}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: bubbleTextColor }} />
                <Box sx={{ width: isMobile ? '56vw' : 320 }}>
                  <Skeleton variant="text" animation="wave" sx={{ bgcolor: loaderSkeletonLight }} />
                  <Skeleton variant="text" width="60%" animation="wave" sx={{ bgcolor: loaderSkeletonLight2 }} />
                </Box>
              </Box>
            )
          ) : (
            showRenderedText && (<Typography variant="body1" sx={{ color: bubbleTextColor }}>{finalRenderedText}</Typography>)
          )}
        <Box sx={{ '& .chat-inline-card': { backgroundColor: cardBg, color: cardTextColor } }}>{renderedWidgets}</Box>
        {allImgs.length > 0 && (
          <Box sx={{ mt: showRenderedText ? 1 : 0 }}>
            {allImgs.map((src, idx) => (
              <Box key={idx} sx={{ mt: idx ? 1 : 0 }}>
                <Box
                  component="img"
                  src={src}
                  alt={`image-${idx + 1}`}
                  onClick={() => onImgClick(src)}
                  sx={{ maxWidth: '100%', borderRadius: 1, cursor: 'zoom-in' }}
                />
              </Box>
            ))}
          </Box>
        )}
        {message.card && message.card.cardsV2 && (
          <Box sx={{ mt: message.text || (message.attachments||[]).length ? 1.5 : 0 }}>
            {message.card.cardsV2.map((c, i) => {
              const header = c.card?.header || {};
              const sections = c.card?.sections || [];
              return (
                <Card
                  key={i}
                  className="chat-inline-card"
                  sx={{
                    mb: 1,
                    maxWidth: isMobile ? '84vw' : '100%',
                    bgcolor: cardBg,
                    color: cardTextColor,
                    '& .MuiTypography-root': { color: cardTextColor },
                    '& .MuiButton-root': { color: cardTextColor, borderColor: cardTextColor },
                  }}
                >
                  {(header.title || header.subtitle || header.imageUrl) && (
                    <CardHeader
                      avatar={header.imageUrl ? (
                        <Avatar src={header.imageUrl} alt="" />
                      ) : undefined}
                      title={header.title || 'Card'}
                      subheader={header.subtitle || ''}
                      sx={{ color: cardTextColor }}
                    />
                  )}
                  <CardContent sx={{ color: cardTextColor }}>
                    {actionLoading && (<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}><CircularProgress size={16} sx={{ color: cardTextColor }} /></Box>)}
                    {sections.map((sec, si) => (
                      <Box key={si} sx={{ mb: 1 }}>
                        {(sec.widgets || []).map((w, wi) => {
                          if (w.keyValue) {
                            const kv = w.keyValue;
                            return (
                              <Box key={wi} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, pr: 1 }}>{kv.topLabel || ''}</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right', flex: 1 }}>{kv.content || ''}</Typography>
                              </Box>
                            );
                          }
                          if (w.textParagraph) {
                            return <Typography key={wi} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{w.textParagraph.text}</Typography>;
                          }
                          if (w.image) {
                            return <Box key={wi} component="img" src={w.image.imageUrl} alt="" sx={{ maxWidth: '100%', borderRadius: 1 }} />;
                          }
                          if (w.decoratedText) {
                            const buttonConfig = w.decoratedText?.button || {};
                            const btn = buttonConfig?.onClick?.action;
                            const buttonLabel = (buttonConfig?.text || '').trim() || 'Select';
                            const textValue = (w.decoratedText?.text || '').trim();
                            const showText = Boolean(textValue && textValue !== buttonLabel);
                            if (textValue) widgetTextFilters.push(textValue);
                            if (buttonLabel) widgetTextFilters.push(buttonLabel);
                            return (
                              <Box key={wi} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {showText && (
                                  <Typography variant="body2" sx={{ flex: 1 }}>{textValue}</Typography>
                                )}
                                {btn && (
                                  <Button size="small" variant="outlined" onClick={() => handleAction(btn)} disabled={actionLoading}>
                                    {buttonLabel}
                                  </Button>
                                )}
                              </Box>
                            );
                          }
                          if (w.selectionInput) {
                            const siw = w.selectionInput;
                            const name = siw.name || `sel_${wi}`;
                            const selected = formState[name] ?? (siw.items?.find(i => i.selected)?.value || '');
                            if ((siw.type || '').toUpperCase() === 'DROPDOWN') {
                              return (
                                <Box key={wi} sx={{ my: 0.5 }}>
                                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                                  <Select size="small" fullWidth value={selected} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} disabled={actionLoading}>
                                    {(siw.items || []).map((item, idx) => (
                                      <MenuItem key={idx} value={item.value || item.text}>{item.text || item.value}</MenuItem>
                                    ))}
                                  </Select>
                                </Box>
                              );
                            }
                            if ((siw.type || '').toUpperCase() === 'RADIO_BUTTON') {
                              return (
                                <Box key={wi} sx={{ my: 0.5 }}>
                                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                                  <RadioGroup row value={selected} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))}>
                                    {(siw.items || []).map((item, idx) => (
                                      <FormControlLabel key={idx} value={item.value || item.text} control={<Radio size="small" disabled={actionLoading} />} label={item.text || item.value} />
                                    ))}
                                  </RadioGroup>
                                </Box>
                              );
                            }
                            if ((siw.type || '').toUpperCase() === 'CHECK_BOX') {
                              const values = Array.isArray(formState[name]) ? formState[name] : [];
                              const toggle = (val) => setFormState(s => {
                                const arr = new Set(Array.isArray(s[name]) ? s[name] : []);
                                if (arr.has(val)) arr.delete(val); else arr.add(val);
                                return { ...s, [name]: Array.from(arr) };
                              });
                              return (
                                <Box key={wi} sx={{ my: 0.5 }}>
                                  {siw.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{siw.label}</Typography>)}
                                  <FormGroup row>
                                    {(siw.items || []).map((item, idx) => (
                                      <FormControlLabel key={idx} control={<Checkbox size="small" checked={values.includes(item.value || item.text)} onChange={() => toggle(item.value || item.text)} disabled={actionLoading} />} label={item.text || item.value} />
                                    ))}
                                  </FormGroup>
                                </Box>
                              );
                            }
                            return null;
                          }
                          if (w.textInput) {
                            const ti = w.textInput;
                            const name = ti.name || `txt_${wi}`;
                            const val = formState[name] ?? '';
                            const multiline = (ti.type || '').toUpperCase() === 'MULTIPLE_LINE';
                            return (
                              <Box key={wi} sx={{ my: 0.5 }}>
                                {ti.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{ti.label}</Typography>)}
                                <TextField fullWidth size="small" value={val} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} multiline={multiline} minRows={multiline ? 3 : 1} disabled={actionLoading} />
                              </Box>
                            );
                          }
                          if (w.dateTimePicker) {
                            const dt = w.dateTimePicker;
                            const name = dt.name || `dt_${wi}`;
                            const val = formState[name] ?? '';
                            const type = (dt.type || '').toUpperCase() === 'DATE_AND_TIME' ? 'datetime-local' : 'date';
                            return (
                              <Box key={wi} sx={{ my: 0.5 }}>
                                {dt.label && (<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{dt.label}</Typography>)}
                                <TextField type={type} size="small" value={val} onChange={(e) => setFormState(s => ({ ...s, [name]: e.target.value }))} disabled={actionLoading} />
                              </Box>
                            );
                          }
                          if (w.buttonList) {
                            const bl = w.buttonList;
                            return (
                              <Box key={wi} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                {(bl.buttons || []).map((b, bi) => {
                                if (b?.text) widgetTextFilters.push(String(b.text));
                                  const act = b?.onClick?.action;
                                  return (
                                    <Button key={bi} size="small" variant="contained" onClick={() => act && handleAction(act)} disabled={actionLoading}>
                                      {b.text || 'Submit'}
                                    </Button>
                                  );
                                })}
                              </Box>
                            );
                          }
                          return null;
                        })}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
      {isMe && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: effectiveUserAccent, color: userContrastText }}>
          {userInitial}
        </Avatar>
      )}
    </Box>
    <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'common.black' }}>
        {lightboxSrc ? (
          <Box component="img" src={lightboxSrc} alt="Attachment preview" sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
        ) : null}
      </DialogContent>
    </Dialog>
    </>
  );
}

// New chat component that calls /webkit/message and supports thread selection
function PolarisChat() {
  const { user, appUser, loading } = useAuth();
  const { chatkitOptions, updateChatkitOptions, resetChatkitOptions } = useChatKitTheme();
  const theme = useTheme();
  const isMobile = (typeof window !== 'undefined') && window.matchMedia && window.matchMedia('(max-width:600px)').matches;
  const THREAD_CONTROLS_ENABLED = chatkitOptions?.history?.enabled !== false;
  const [activeThreads, setActiveThreads] = useState([]);
  // Map of local thread id -> OpenAI conversation id
  const [convByThread, setConvByThread] = useState({});
  const [remoteThreadByLocal, setRemoteThreadByLocal] = useState({});
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [threadNames, setThreadNames] = useState({});
  const [hiddenThreads, setHiddenThreads] = useState(new Set());
  const [messagesByThread, setMessagesByThread] = useState({});
  const [lastAssistantByThread, setLastAssistantByThread] = useState({});
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const listRef = useRef(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

  const forceScrollToBottom = useCallback(() => {
    const node = listRef.current;
    if (!node) return;
    requestAnimationFrame(() => {
      if (!listRef.current) return;
      listRef.current.scrollTop = listRef.current.scrollHeight;
      setIsPinnedToBottom(true);
    });
  }, []);
  const fileInputRef = useRef(null);
  const initialThreadInitialized = useRef(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [creatingThread, setCreatingThread] = useState(false);
  const [threadMenuEl, setThreadMenuEl] = useState(null);
  const [toolsMenuEl, setToolsMenuEl] = useState(null);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [selectedToolKey, setSelectedToolKey] = useState('');
  const [toolForm, setToolForm] = useState({});
  const [toolSubmitting, setToolSubmitting] = useState(false);
  // --- Thread modals (rename/new/delete) ---
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameTargetId, setRenameTargetId] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newDraft, setNewDraft] = useState('');
  const [newCreating, setNewCreating] = useState(false);
  const [actionsMenuEl, setActionsMenuEl] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const initialThreadFromQuery = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('threadId') || params.get('thread_id');
    } catch (_) {
      return null;
    }
  }, []);
  const initialThreadFromContext = useMemo(() => {
    if (!appUser) return null;
    if (appUser.threadId) return appUser.threadId;
    if (Array.isArray(appUser.threadIds) && appUser.threadIds.length) return appUser.threadIds[0];
    return null;
  }, [appUser]);
  const storedThreadId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem('polarisThreadId');
    } catch (_) {
      return null;
    }
  }, [user?.email, appUser?.userId]);

  const rawAccentColor = chatkitOptions?.theme?.color?.accent?.primary;
  const accentColor = rawAccentColor || theme.palette.primary.main;
  const colorScheme = chatkitOptions?.theme?.colorScheme || theme.palette.mode;
  const defaultAccentNormalized = normalizeColorString(defaultChatKitOptions.theme.color.accent.primary);
  const accentNormalized = normalizeColorString(accentColor);
  const accentIsDefault = accentNormalized === defaultAccentNormalized;
  const startAccentColor = (colorScheme === 'dark' && accentIsDefault) ? '#7c4dff' : accentColor;
  const composerPlaceholder = chatkitOptions?.composer?.placeholder || 'Type a message…';
  const attachmentsConfig = chatkitOptions?.composer?.attachments || {};
  const attachmentsEnabledSetting = attachmentsConfig.enabled !== false;
  const attachmentsEnabled = !isMobile && attachmentsEnabledSetting;
  const startGreeting = chatkitOptions?.startScreen?.greeting || 'Welcome! I\'m Polaris, How can I help today?';
  const startPrompts = Array.isArray(chatkitOptions?.startScreen?.prompts) ? chatkitOptions.startScreen.prompts : [];
  const promptTextColor = theme.palette.getContrastText ? theme.palette.getContrastText(startAccentColor) : '#ffffff';
  const composerSize = chatkitOptions?.theme?.density === 'compact' ? 'small' : chatkitOptions?.theme?.density === 'spacious' ? 'large' : 'medium';

  const meEmail = (user?.email || '').toLowerCase();
  const meName = user?.name || meEmail?.split('@')[0] || 'You';

  // Uses module-level parseMessageTimestamp()

  const formatSendError = useCallback((error) => {
    const msg = typeof error === 'string' ? error : error?.message || String(error);
    if (/failed to fetch|network error|networkerror|504|timeout|timed out|gateway timeout/i.test(msg || '')) {
      return 'Polaris is taking longer than expected. Please try again in a moment.';
    }
    return msg || 'Something went wrong. Please try again.';
  }, []);

  const extractRemoteThreadId = useCallback((payload) => {
    if (!payload) return null;
    const pushCandidate = (arr, value) => {
      if (typeof value === 'string' && value.trim()) arr.push(value.trim());
    };
    const candidates = [];
    if (typeof payload === 'string') {
      pushCandidate(candidates, payload);
    } else if (typeof payload === 'object') {
      const keys = [
        'thread_id',
        'threadId',
        'assistant_thread_id',
        'assistantThreadId',
        'openai_thread_id',
        'openaiThreadId',
        'conversation_id',
        'conversationId'
      ];
      keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          pushCandidate(candidates, payload[key]);
        }
      });
    }
    const found = candidates.find((value) => /^thread[_-]/i.test(value));
    return found || null;
  }, []);

  const registerRemoteThread = useCallback((localId, remoteId) => {
    if (!localId || !remoteId) return;
    setRemoteThreadByLocal((prev) => {
      if (prev[localId] === remoteId) return prev;
      return { ...prev, [localId]: remoteId };
    });
  }, []);

  const updateThreadAssociations = useCallback((localThread, payload) => {
    if (!localThread || !payload) return;
    const convCandidateRaw = typeof payload === 'object'
      ? (payload.conversation_id || payload.assistant_thread_id || payload.conversationId || payload.assistantThreadId || null)
      : null;
    const convCandidate = convCandidateRaw ? String(convCandidateRaw) : null;
    if (convCandidate) {
      setConvByThread((prev) => ({ ...prev, [localThread]: convCandidate }));
    }
    const remoteId = extractRemoteThreadId(payload)
      || (typeof convCandidate === 'string' && /^thread[_-]/i.test(convCandidate.trim()) ? convCandidate.trim() : null);
    if (remoteId) {
      registerRemoteThread(localThread, remoteId);
    }
  }, [extractRemoteThreadId, registerRemoteThread]);

  const getRemoteThreadId = useCallback((localThread) => {
    if (!localThread) return null;
    const mapped = remoteThreadByLocal[localThread];
    if (mapped) return mapped;
    const convId = convByThread[localThread];
    if (typeof convId === 'string' && /^thread[_-]/i.test(convId.trim())) {
      return convId.trim();
    }
    return null;
  }, [remoteThreadByLocal, convByThread]);

  const normalizeAttachmentFingerprint = useCallback((attachments) => {
    const values = (attachments || [])
      .map(att => (att?.url || att?.dataUrl || '').trim())
      .filter(Boolean);
    if (values.length <= 1) return values;
    return Array.from(new Set(values)).sort();
  }, []);

  const loaderTimersRef = useRef({});
  const clearLoaderTimers = useCallback((loaderId) => {
    const timers = loaderTimersRef.current[loaderId];
    if (timers) {
      timers.forEach((timerId) => clearTimeout(timerId));
      delete loaderTimersRef.current[loaderId];
    }
  }, []);

  const setLoaderStatus = useCallback((threadId, loaderId, text, persist = true) => {
    if (!threadId || !loaderId) return;
    setMessagesByThread(prev => {
      const list = prev[threadId];
      if (!Array.isArray(list)) return prev;
      let modified = false;
      const updated = list.map(msg => {
        if (msg.id !== loaderId) return msg;
        modified = true;
        return { ...msg, text, loading: persist };
      });
      if (!modified) return prev;
      const next = { ...prev };
      next[threadId] = updated;
      return next;
    });
  }, [setMessagesByThread]);

  const scheduleLoaderStatusUpdates = useCallback((threadId, loaderId) => {
    if (!threadId || !loaderId) return;
    clearLoaderTimers(loaderId);
    const timers = [
      setTimeout(() => {
        setLoaderStatus(threadId, loaderId, 'working on this…');
      }, 15000),
      setTimeout(() => {
        setLoaderStatus(threadId, loaderId, 'Still processing your request, thanks for your patience.');
      }, 450000),
    ];
    loaderTimersRef.current[loaderId] = timers;
  }, [clearLoaderTimers, setLoaderStatus]);

  useEffect(() => () => {
    Object.values(loaderTimersRef.current).forEach((timers) => {
      if (Array.isArray(timers)) {
        timers.forEach((timerId) => clearTimeout(timerId));
      }
    });
    loaderTimersRef.current = {};
  }, []);

  const computeAssistantFingerprint = useCallback((message) => {
    if (!message) return '';
    const textKey = typeof message.text === 'string'
      ? message.text.replace(/\s+/g, ' ').trim()
      : '';
    const attachmentsKey = normalizeAttachmentFingerprint(message.attachments).join('|');
    const cardKey = message.card ? JSON.stringify(message.card) : '';
    const widgetsKey = Array.isArray(message.widgets) && message.widgets.length
      ? JSON.stringify(message.widgets)
      : '';
    return [textKey, attachmentsKey, cardKey, widgetsKey].filter(Boolean).join('||');
  }, [normalizeAttachmentFingerprint]);

  const pushAssistantMessage = useCallback((threadId, message, externalId) => {
    if (!threadId || !message) return false;
    const fingerprint = computeAssistantFingerprint(message);
    const meta = lastAssistantByThread[threadId];
    let appended = false;
    setMessagesByThread(prev => {
      const list = prev[threadId] || [];
      const lastAssistant = [...list].reverse().find(msg => String(msg?.sender || '').toLowerCase() === 'polaris-bot');
      const lastFingerprint = computeAssistantFingerprint(lastAssistant);
      const duplicateById = Boolean(externalId) && (
        (lastAssistant && lastAssistant.externalMessageId === externalId) ||
        (meta?.messageId && meta.messageId === externalId)
      );
      const duplicateByFingerprint = !externalId && (
        (lastFingerprint && lastFingerprint === fingerprint) ||
        (meta?.fingerprint && meta.fingerprint === fingerprint)
      );
      const duplicateInList = list.some(existing => {
        if (String(existing?.sender || '').toLowerCase() !== 'polaris-bot') return false;
        if (externalId && existing.externalMessageId && existing.externalMessageId === externalId) {
          return true;
        }
        const existingFp = computeAssistantFingerprint(existing);
        return existingFp && existingFp === fingerprint;
      });
      if (duplicateById || duplicateByFingerprint || duplicateInList) {
        return prev;
      }
      const filtered = list.filter(msg => !msg.loading);
      const next = { ...prev, [threadId]: [...filtered, message] };
      appended = true;
      return next;
    });
    setLastAssistantByThread(prev => {
      const existing = prev[threadId] || {};
      const nextMeta = appended
        ? { messageId: externalId || null, fingerprint }
        : { messageId: externalId || existing.messageId || null, fingerprint: fingerprint || existing.fingerprint || '' };
      if (existing.messageId === nextMeta.messageId && existing.fingerprint === nextMeta.fingerprint) {
        return prev;
      }
      return { ...prev, [threadId]: nextMeta };
    });
    return appended;
  }, [computeAssistantFingerprint, lastAssistantByThread, setMessagesByThread, setLastAssistantByThread]);

  const TOOL_OPTIONS = useMemo(() => ([
    { key: 'sendEscalationRequestForm', label: 'IT Support Ticket', description: 'Open an IT ticket and track status.' },
    { key: 'isPlanCommissionable', label: 'Commissionable Plan Check', description: 'Check if a MAPD plan is commissionable.' },
    { key: 'sendElectionPeriodRequest', label: 'SEP Request', description: 'Evaluate available Special Enrollment Periods.' },
    { key: 'sendORCRequestCard', label: 'Retirement Coverage Check', description: 'Check retiree coverage coordination' },
    { key: 'triggerSPAPCard', label: 'SPAP Card', description: 'Get a state SPAP card.' },
    // Unified-only tool (message route)
    // { key: 'SET_REMINDER', label: 'Set Reminder', description: 'Schedule a reminder with date and time.' },
  ]), []);

  // Field configs per tool
  const STATES = useMemo(() => [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
  ], []);

  const TOOL_DEFS = useMemo(() => ({
    sendEscalationRequestForm: {
      route: 'cardAction', fn: 'onITescalateSubmitCard', title: 'Support Ticket Card',
      fields: [
        { name:'issueDescription', label:'Describe the issue in detail for the support team', type:'textarea', required:true },
        { name:'frequency', label:'How often do you have this issue?', type:'textarea' },
        { name:'attempted', label:'Have you attempted to solve the problem using the troubleshooting steps I sent you?', type:'radio', options:['Yes','No'], default:'No' },
      ],
    },
    isPlanCommissionable: {
      route: 'cardAction', fn: 'onMAPDcheckSubmit', title: 'Commissionable Plan Check',
      fields: [
        { name:'state', label:'State - Abbreviated', type:'text', required:true },
        { name:'service_area', label:'County of Residence', type:'text', required:true },
        { name:'plan_id', label:'Plan ID', type:'text', required:true },
        { name:'carrier', label:'Insurance Carrier', type:'select', options:['Aetna','HealthSpring','Centene','Devoted','Elevance','Humana','UHC','SCAN'], required:true },
      ],
    },
    triggerPlanResearch: {
      route: 'cardAction', fn: 'onPlanResearchSubmit', title: 'MAPD Plan Research',
      fields: [
        { name:'planName', label:'Carrier / Plan Name', type:'text' },
        { name:'planId', label:'Plan ID (H- or R-code)', type:'text' },
        { name:'planQuestion', label:'What do you need to know?', type:'textarea' },
      ],
    },
    triggerProviderNetworkResearch: {
      route: 'cardAction', fn: 'onProviderNetworkLookupSubmit', title: 'Provider Directory Lookup',
      fields: [
        { name:'firstName', label:'Provider First Name', type:'text', required:true },
        { name:'lastName', label:'Provider Last Name', type:'text', required:true },
        { name:'carrierPlan', label:'Carrier / Plan Name', type:'text', required:true },
        { name:'city', label:'City', type:'text', required:true },
      ],
    },
    sendElectionPeriodRequest: {
      route: 'cardAction', fn: 'onSubmitCard', title: 'SEP Request',
      fields: [
        { name:'state', label:'State of Residence', type:'select', options:STATES, required:true },
        { name:'service_area', label:'County of Residence', type:'text', required:true },
        { name:'carrier', label:'Insurance Carrier', type:'select', options:['Aetna','Cigna','Centene','Devoted','Elevance','Humana','UHC','SCAN'], required:true },
        { name:'veteranStatus', label:'VA/Tricare/ChampVA?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'recentMove', label:'Has the client recently moved or is planning to move in the next 30 days?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'5star', label:'Intended new plan a 5-Star rated plan?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'integratedCareSep', label:'Is the intended NEW plan an Integrated DSNP, and, Does the client have FULL Medicaid and is eligible? (HIDE or FIDE)', type:'radio', options:['Yes','No'], default:'No' },
        { name:'NLS_sep', label:'Has the client had a recent downgrade or upgrade to their Extra Help/LIS?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'MCD_sep', label:'Has the client had a recent downgrade or upgrade to their Medicaid?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'missedAEP', label:'Has the client missed one of the following election periods: AEP, IEP, OEP, or ICEP?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'chronic', label:'Is the intended new plan a C-SNP?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'oepChange', label:'Has the client made a change to their MA/MAPD within the first 3 months of the year?', type:'radio', options:['Yes','No'], default:'No' },
        { name:'currentPlan', label:'Select which kind of plan the client is on CURRENTLY', type:'radio', options:['Original Medicare','MA only','MAPD','DSNP','CSNP'] },
      ],
    },
    triggerRXResearch: {
      route: 'cardAction', fn: 'onRXResearchSubmit', title: 'Medication / Pharmacy Research',
      fields: [
        { name:'pharmacy', label:'Pharmacy Name (optional)', type:'text' },
        { name:'carrier', label:'Insurance Carrier (for pharmacy lookup)', type:'text' },
        { name:'medication', label:'Medication (w/ dosage)', type:'text' },
        { name:'planName', label:'MAPD Plan Name', type:'text' },
        { name:'planId', label:'Plan ID (H- or R-code)', type:'text' },
      ],
    },
    sendORCRequestCard: {
      route: 'cardAction', fn: 'onORCSubmitCard', title: 'Retiree Coverage Check (ORC)',
      fields: [
        { name:'state', label:'State of Residence', type:'select', options:STATES, required:true },
        { name:'employer_plan_name_value', label:'Employer Plan Name', type:'text' },
        { name:'employer_name_value', label:'Job title or Former Employer', type:'text' },
      ],
    },
    triggerSPAPCard: {
      route: 'cardAction', fn: 'onSPAPCardSubmit', title: 'SPAP Card (State Program)',
      fields: [
        { name:'state', label:'State', type:'select', options:STATES, required:true },
      ],
    },
    triggerMap: {
      route: 'cardAction', fn: 'onMapQuestionSubmit', title: 'Maps & Service Areas',
      fields: [
        { name:'mapQuestion', label:'Your map/geography question', type:'textarea', required:true },
      ],
    },
    routeToAssistant: {
      route: 'cardAction', fn: 'onSpecificQuestionSubmit', title: 'Ask a Specific Question',
      fields: [
        { name:'userQuestion', label:'Your question', type:'textarea', required:true },
      ],
    },
    HUMAN_RESOURCES: {
      route: 'messages', title: 'HR Notice',
      fields: [ { name:'reason', label:'What do you need HR to know?', type:'textarea', required:true } ],
      buildMessage: (f) => `HR notice: ${f.reason || ''}`,
    },
    SET_REMINDER: {
      route: 'messages', title: 'Set Reminder',
      fields: [
        { name:'reminder_date', label:'Date (YYYY-MM-DD)', type:'date', required:true },
        { name:'reminder_time', label:'Time (HH:MM 24h)', type:'time', required:true },
        { name:'reminder_text', label:'What should I remind you about?', type:'textarea', required:true },
      ],
      buildMessage: (f) => `Remind me on ${f.reminder_date || ''} at ${f.reminder_time || ''} about: ${f.reminder_text || ''}`,
    },
  }), [STATES]);

  const buildWidgetsFromResponse = useCallback((toolKey, data, attachments) => {
    if (Array.isArray(data?.widgets)) {
      return data.widgets.filter(Boolean);
    }
    const widgets = [];
    if (toolKey === 'triggerSPAPCard') {
      let attachmentUrl = null;
      if (Array.isArray(attachments) && attachments.length > 0) {
        const first = attachments[0];
        if (first && typeof first === 'object') {
          attachmentUrl = first.url || null;
        }
      }
      if (data && typeof data === 'object') {
        attachmentUrl = attachmentUrl || data.spap_card_url || data.spapImageUrl || null;
      }
      if (attachmentUrl) {
        widgets.push({
          type: 'image',
          src: attachmentUrl,
          alt: 'State Pharmaceutical Assistance Program card',
          frame: true,
          fit: 'contain',
          maxWidth: '360px',
        });
      }
    }
    if (toolKey === 'SET_REMINDER') {
      const textValue = typeof data === 'object' && data !== null
        ? String(data.assistant_text || '')
        : String(data || '');
      const lines = textValue
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      if (lines.length) {
        widgets.push({
          type: 'card',
          title: 'Reminder scheduled',
          body: lines,
        });
      }
    }
    return widgets;
  }, []);

  // Local storage helpers for thread preferences (names + hidden status)
  const prefsKey = useMemo(() => (meEmail ? `polaris.threadPrefs:${meEmail}` : null), [meEmail]);
  const loadPrefs = useCallback(() => {
    try {
      if (!prefsKey) return { names: {}, hidden: [] };
      const raw = localStorage.getItem(prefsKey);
      if (!raw) return { names: {}, hidden: [] };
      const obj = JSON.parse(raw);
      return { names: obj.names || {}, hidden: Array.isArray(obj.hidden) ? obj.hidden : [] };
    } catch (_) { return { names: {}, hidden: [] }; }
  }, [prefsKey]);
  const savePrefs = useCallback((names, hiddenArr) => {
    try {
      if (!prefsKey) return;
      localStorage.setItem(prefsKey, JSON.stringify({ names, hidden: hiddenArr }));
    } catch (_) {}
  }, [prefsKey]);
  const displayNameForThread = useCallback((tid, idx = 0) => {
    const n = threadNames[tid];
    if (n && String(n).trim()) return n;
    if (tid && typeof tid === 'string' && tid.startsWith('localThread_')) {
      const short = tid.slice(-6);
      return `Chat ${idx + 1} • ${short}`;
    }
    return `Chat ${idx + 1}`;
  }, [threadNames]);
  const assignDefaultNames = useCallback((ids) => {
    setThreadNames(prev => {
      const next = { ...prev };
      ids.forEach((tid, i) => {
        if (!next[tid]) next[tid] = (tid && tid.startsWith('localThread_')) ? `Chat ${i + 1} • ${tid.slice(-6)}` : `Chat ${i + 1}`;
      });
      const { hidden } = loadPrefs();
      savePrefs(next, hidden);
      return next;
    });
  }, [loadPrefs, savePrefs]);
  const ensureThreadHydrated = useCallback((threadId, preferredName) => {
    const normalized = typeof threadId === 'string' ? threadId.trim() : '';
    if (!normalized) return false;
    const trimmedName = preferredName && String(preferredName).trim();
    const defaultLabel = trimmedName
      || (normalized.startsWith('localThread_') ? `Chat 1 • ${normalized.slice(-6)}` : 'Polaris Chat');
    setActiveThreads([normalized]);
    setHiddenThreads(() => {
      const cleared = new Set();
      const { names } = loadPrefs();
      savePrefs(names, []);
      return cleared;
    });
    setThreadNames(prev => {
      const next = { ...prev };
      if (!next[normalized]) {
        next[normalized] = defaultLabel;
      }
      const { hidden } = loadPrefs();
      savePrefs(next, hidden);
      return next;
    });
    setMessagesByThread(prev => {
      if (prev[normalized]) return prev;
      return { ...prev, [normalized]: [] };
    });
    setSelectedThreadId(normalized);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('polarisThreadId', normalized);
      } catch (_) {}
    }
    return true;
  }, [loadPrefs, savePrefs]);
  const renameThread = useCallback((tid, newName) => {
    setThreadNames(prev => {
      const next = { ...prev, [tid]: newName };
      const { hidden } = loadPrefs();
      savePrefs(next, hidden);
      return next;
    });
  }, [loadPrefs, savePrefs]);
  const hideThread = useCallback((tid) => {
    setHiddenThreads(prev => {
      const next = new Set([...prev, tid]);
      const { names } = loadPrefs();
      savePrefs(names, Array.from(next));
      return next;
    });
  }, [loadPrefs, savePrefs]);
  // Load saved prefs on user change
  useEffect(() => {
    const { names, hidden } = loadPrefs();
    setThreadNames(names || {});
    setHiddenThreads(new Set(hidden || []));
  }, [loadPrefs]);

  // Apply prefs to active thread list and selection
  useEffect(() => {
    const visible = activeThreads.filter(t => !hiddenThreads.has(t));
    assignDefaultNames(visible);
    if (visible.length !== activeThreads.length) {
      setActiveThreads(visible);
      return;
    }
    if (!selectedThreadId && visible.length > 0) {
      setSelectedThreadId(visible[0]);
    } else if (selectedThreadId && !visible.includes(selectedThreadId)) {
      setSelectedThreadId(visible[0] || '');
    }
  }, [activeThreads, hiddenThreads, selectedThreadId, assignDefaultNames]);

  useEffect(() => {
    if (!selectedThreadId) return;
    if (messagesByThread[selectedThreadId] !== undefined) return;
    const welcome = { id: Date.now(), sender: 'polaris-bot', senderName: 'Polaris', text: 'Welcome to Polaris Chat! How can I help today?', timestamp: Date.now() };
    const tid = selectedThreadId;
    setMessagesByThread(prev => ({ ...prev, [tid]: [welcome] }));
  }, [selectedThreadId, messagesByThread]);

  const currentMessages = messagesByThread[selectedThreadId] || [];
  const remoteThreadIdForSelected = getRemoteThreadId(selectedThreadId);

  useEffect(() => {
    const node = listRef.current;
    if (!node) return () => {};
    const handleScroll = () => {
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
      setIsPinnedToBottom(distanceFromBottom <= 24);
    };
    handleScroll();
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const node = listRef.current;
    if (!node) return;
    if (!isPinnedToBottom) return;
    node.scrollTop = node.scrollHeight;
  }, [currentMessages, isPinnedToBottom, selectedThreadId]);

  useEffect(() => {
    setIsPinnedToBottom(true);
  }, [selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId) return;
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('polarisThreadId', selectedThreadId);
    } catch (_) {}
  }, [selectedThreadId]);

  const parsePossibleCard = useCallback((text) => {
    if (!text || typeof text !== 'string') return null;
    const trimmed = text.trim();
    try {
      const obj = JSON.parse(trimmed);
      if (obj && (obj.cardsV2 || (obj.card && obj.card.cardsV2))) {
        return obj.cardsV2 ? { cardsV2: obj.cardsV2 } : { cardsV2: obj.card.cardsV2 };
      }
    } catch (_) {}
    try {
      const idx = trimmed.indexOf('"cardsV2"');
      if (idx !== -1) {
        let start = trimmed.lastIndexOf('{', idx);
        let end = trimmed.indexOf('}', idx);
        while (start !== -1 && end !== -1) {
          const candidate = trimmed.slice(start, end + 1);
          try {
            const obj2 = JSON.parse(candidate);
            if (obj2 && obj2.cardsV2) {
              return { cardsV2: obj2.cardsV2 };
            }
          } catch (_) {}
          end = trimmed.indexOf('}', end + 1);
        }
      }
    } catch (_) {}
    return null;
  }, []);

  const createNewThread = useCallback(async (initialName) => {
    setSendError(null);
    setCreatingThread(true);
    try {
      const resp = await fetch(buildApiUrl('/webkit/new-thread'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: meEmail,
          display_name: meName,
        })
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${t}`);
      }
      const data = await resp.json();
      const newIdRaw = data.local_thread_id || '';
      const responseActiveIds = Array.isArray(data.active_thread_ids) && data.active_thread_ids.length
        ? data.active_thread_ids
        : [];
      const responsePrimary = data.active_thread_id || data.thread_id || null;
      const resolvedThreadId = (
        (typeof newIdRaw === 'string' && newIdRaw.trim()) ? newIdRaw.trim()
          : (responsePrimary && String(responsePrimary).trim())
      ) || (responseActiveIds.length ? String(responseActiveIds[0]).trim() : '');
      const trimmedInitial = initialName && String(initialName).trim();
      const hydrated = ensureThreadHydrated(resolvedThreadId, trimmedInitial);
      if (hydrated && resolvedThreadId) {
        const convIdFromCreate = data.conversation_id || data.assistant_thread_id || null;
        if (convIdFromCreate) {
          setConvByThread(prev => ({ ...prev, [resolvedThreadId]: convIdFromCreate }));
        }
        const remoteFromCreate = extractRemoteThreadId(data)
          || (typeof convIdFromCreate === 'string' && /^thread[_-]/i.test(convIdFromCreate.trim()) ? convIdFromCreate.trim() : null);
        if (remoteFromCreate) {
          registerRemoteThread(resolvedThreadId, remoteFromCreate);
        }
        setInput('');
        setPendingImages([]);
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('polarisThreadId', resolvedThreadId);
          } catch (_) {}
        }
      } else {
        setActiveThreads([]);
        setSelectedThreadId('');
      }
    } catch (e) {
      setSendError(formatSendError(e));
    } finally {
      setCreatingThread(false);
    }
  }, [meEmail, meName, formatSendError, extractRemoteThreadId, registerRemoteThread, ensureThreadHydrated]);

  useEffect(() => {
    if (initialThreadInitialized.current) return;
    if (loading || !user || creatingThread) return;
    const preferredThread = initialThreadFromQuery
      || initialThreadFromContext
      || storedThreadId;
    if (ensureThreadHydrated(preferredThread, null)) {
      initialThreadInitialized.current = true;
      return;
    }
    initialThreadInitialized.current = true;
    createNewThread();
  }, [loading, user, creatingThread, initialThreadFromQuery, initialThreadFromContext, storedThreadId, ensureThreadHydrated, createNewThread]);

  // Poll WebKit history for a recent assistant reply if the initial send times out
  const pollForAssistantReply = useCallback(async (threadId, loaderMessageId, sinceEpochMs, maxTries = Infinity, intervalMs = 500) => {
    if (!threadId) return false;
    const since = Number(sinceEpochMs || 0);
    for (let attempt = 0; attempt < maxTries; attempt++) {
      try {
        const resp = await fetch(buildApiUrl('/webkit/history'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: meEmail, thread_id: threadId, limit: 12 })
        });
        if (resp.ok) {
          const data = await resp.json();
          const rows = Array.isArray(data?.rows) ? data.rows : [];
          const found = rows.find(r => {
            const roleLower = String(r.role || '').toLowerCase();
            const isAssistant = roleLower === 'bot' || roleLower === 'assistant' || roleLower === 'polaris';
            if (!isAssistant) return false;
            const ts = parseMessageTimestamp(r.message_timestamp);
            return ts > since;
          });
          if (found) {
            const attachments = [];
            try {
              const raw = found.message_attachments;
              if (raw && typeof raw === 'string') {
                const trimmed = raw.trim();
                if (trimmed.startsWith('[')) {
                  const arr = JSON.parse(trimmed);
                  if (Array.isArray(arr)) attachments.push(...arr.filter(Boolean).map(url => ({ url: String(url) })));
                } else {
                  trimmed.split(/\r?\n|,\s*/).map(u => u.trim()).filter(Boolean).forEach(url => attachments.push({ url }));
                }
              } else if (Array.isArray(found.message_attachments)) {
                attachments.push(...found.message_attachments.filter(Boolean).map(url => ({ url: String(url) })));
              }
            } catch (_) {}
            updateThreadAssociations(threadId, found);
            const externalId = found.message_id || found.assistant_message_id || null;
            const assistantMsg = {
              id: found.message_id || `${found.assistant_message_id || 'assistant'}_${Date.now()}`,
              sender: 'polaris-bot',
              senderName: 'Polaris',
              text: found.message_contents || '',
              attachments,
              timestamp: parseMessageTimestamp(found.message_timestamp) || Date.now(),
              externalMessageId: externalId,
            };
            const appended = pushAssistantMessage(threadId, assistantMsg, externalId);
            if (appended) {
              return true;
            }
            // If the message was a duplicate of an already delivered assistant reply,
            // keep polling until a truly new response arrives.
          }
        }
      } catch (_) {}
      await new Promise(res => setTimeout(res, intervalMs));
    }
    return false;
  }, [meEmail, pushAssistantMessage, updateThreadAssociations]);

const sendMessage = useCallback(async (overrideText) => {
    const messageText = overrideText !== undefined ? overrideText : input;
    const trimmed = (messageText || '').trim();
    const targetThreadId = selectedThreadId;
    if ((!trimmed && pendingImages.length === 0) || !targetThreadId) return;
    setSendError(null);
    const userMsg = { id: Date.now(), sender: meEmail, senderName: meName, text: trimmed, attachments: pendingImages, timestamp: Date.now() };
    const loaderId = `loader_${Date.now()}`;
    const loaderMsg = { id: loaderId, sender: 'polaris-bot', senderName: 'Polaris', text: '', loading: true, timestamp: Date.now() };
    setMessagesByThread(prev => ({
      ...prev,
      [targetThreadId]: [...(prev[targetThreadId] || []), userMsg, loaderMsg]
    }));
    forceScrollToBottom();
    scheduleLoaderStatusUpdates(targetThreadId, loaderId);
    setInput('');
    setSending(true);
    const sendStartedAt = Date.now();
    const remoteThreadIdForSend = getRemoteThreadId(targetThreadId);
    try {
      let textToSend = trimmed || 'Image attached.';
      if (pendingImages.length > 0) {
        const urls = pendingImages.map(att => att?.dataUrl).filter(Boolean).join('\n');
        textToSend = `${textToSend}\n\n[Attachments]\n${urls}`;
      }
      // Use unified handler which routes to unifiedClassificationHandlerWEB server-side
      const resp = await fetch(buildApiUrl('/webkit/messages'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: meEmail,
          message: textToSend,
          display_name: meName,
          thread_id: targetThreadId || undefined,
          local_thread_id: targetThreadId || undefined,
          assistant_thread_id: remoteThreadIdForSend || undefined,
          conversation_id: convByThread[targetThreadId] || undefined,
          attachments: (pendingImages || []).map(a => ({
            name: a.name,
            content_type: a.type,
            size: a.size,
            data_url: a.dataUrl
          }))
        })
      });
      if (!resp.ok) {
        const t = await resp.text();
        const err = new Error(`HTTP ${resp.status}: ${t}`);
        err.status = resp.status;
        err.body = t;
        throw err;
      }
      if (false) {
        // Stream tokens and update the loader message incrementally
        const reader = resp.body?.getReader?.();
      if (reader) {
        const decoder = new TextDecoder();
        let buffer = '';
        let acc = '';
        const safeParse = (txt) => { try { return JSON.parse(txt); } catch (_) { return null; } };
          const updateLoader = (t) => setMessagesByThread(prev => {
            const base = { ...prev };
            const list = base[targetThreadId] || [];
            base[targetThreadId] = list.map(m => m.id === loaderId ? { ...m, text: t, loading: false } : m);
            return base;
          });
          const finalizeCard = (cardPayload) => setMessagesByThread(prev => {
            const base = { ...prev };
            // Remove loader and append card
            base[targetThreadId] = (base[targetThreadId] || []).filter(m => m.id !== loaderId && !m.loading);
            base[targetThreadId] = [...(base[targetThreadId] || []), { id: `card_${Date.now()}`, sender: 'polaris-bot', senderName: 'Polaris', text: '', card: cardPayload, timestamp: Date.now() }];
            return base;
          });
          const tryHandleJson = (line) => {
            const obj = safeParse(line);
            if (!obj) return false;
            const localThread = obj.local_thread_id || targetThreadId;
            if (localThread) setActiveThreads([localThread]);
            if (localThread && localThread !== targetThreadId) setSelectedThreadId(localThread);
            updateThreadAssociations(localThread, obj);
            const t = obj.delta || obj.text || obj.assistant_text || '';
            if (t) { acc += String(t); updateLoader(acc); }
            const cardPayload = obj.cardsV2 ? { cardsV2: obj.cardsV2 } : (obj.card_json || obj.card || (obj.cards ? { cardsV2: obj.cards } : null));
            if (cardPayload) { finalizeCard(cardPayload); }
            return true;
          };
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buffer.indexOf('\n')) !== -1) {
              const line = buffer.slice(0, idx).trim();
              buffer = buffer.slice(idx + 1);
              if (!line) continue;
              if (line.startsWith('data:')) {
                const payload = line.slice(5).trim();
                if (payload === '[DONE]') continue;
                if (!tryHandleJson(payload)) { acc += payload; updateLoader(acc); }
              } else {
                if (!tryHandleJson(line)) { acc += line; updateLoader(acc); }
              }
            }
          }
          const rest = buffer.trim();
          if (rest) {
            if (!tryHandleJson(rest)) { acc += rest; updateLoader(acc); }
          }
        }
      } else {
        const data = await resp.json();
        const serverThreadId = data.local_thread_id || null;
        const threadChanged = Boolean(serverThreadId && serverThreadId !== targetThreadId);
        const finalThreadId = threadChanged ? serverThreadId : targetThreadId;
        const remoteFromData = extractRemoteThreadId(data) || null;
        const capturedTexts = Array.isArray(data.captured_texts)
          ? data.captured_texts
              .map((t) => (typeof t === 'string' ? t : (t != null ? String(t) : '')))
              .map((t) => t.trim())
              .filter(Boolean)
          : [];
        const respAttachments = Array.isArray(data.attachment_urls)
          ? data.attachment_urls.filter(Boolean).map(u => ({ url: String(u) }))
          : [];
        let assistantText = typeof data.assistant_text === 'string' ? data.assistant_text : '';
        if (!assistantText && capturedTexts.length) {
          assistantText = capturedTexts.join('\n\n');
        }
        const assistantTextTrimmed = assistantText.trim();
        const externalId = data.assistant_message_id || data.response_id || null;
        const widgets = buildWidgetsFromResponse(null, data, respAttachments);
        const cardPayload = (data && (data.cardsV2 || data.card_json || data.card || data.cards))
          ? (data.cardsV2 ? { cardsV2: data.cardsV2 } : (data.card_json || data.card || (data.cards ? { cardsV2: data.cards } : null)))
          : null;
        let hasImmediateAssistantContent = Boolean(
          widgets.length
          || cardPayload
          || assistantTextTrimmed
          || respAttachments.length
        );

        setMessagesByThread(prev => {
          const base = { ...prev };
          const source = (prev[targetThreadId] || []).slice();
          if (threadChanged) {
            base[finalThreadId] = source;
            delete base[targetThreadId];
          } else {
            base[finalThreadId] = source;
          }
          return base;
        });

        if (threadChanged) {
          setThreadNames(prev => {
            const next = { ...prev };
            if (next[targetThreadId] && !next[finalThreadId]) {
              next[finalThreadId] = next[targetThreadId];
            }
            delete next[targetThreadId];
            const { hidden } = loadPrefs();
            savePrefs(next, hidden);
            return next;
          });
          setHiddenThreads(prev => {
            if (!prev.has(targetThreadId)) return prev;
            const next = new Set(prev);
            next.delete(targetThreadId);
            next.add(finalThreadId);
            const { names } = loadPrefs();
            savePrefs(names, Array.from(next));
            return next;
          });
          setActiveThreads(prev => {
            const filtered = prev.filter(id => id !== targetThreadId);
            if (!filtered.includes(finalThreadId)) {
              filtered.unshift(finalThreadId);
            }
            return filtered;
          });
          setSelectedThreadId(finalThreadId);
        }

        updateThreadAssociations(finalThreadId, data);

        if (hasImmediateAssistantContent) {
          clearLoaderTimers(loaderId);
          let appended = false;
          if (widgets.length) {
            const botMsg = {
              id: Date.now() + 1,
              sender: 'polaris-bot',
              senderName: 'Polaris',
              text: assistantTextTrimmed,
              widgets,
              attachments: respAttachments,
              timestamp: Date.now(),
              externalMessageId: externalId,
            };
            appended = pushAssistantMessage(finalThreadId, botMsg, externalId);
          } else if (cardPayload) {
            const botCard = {
              id: Date.now() + 1,
              sender: 'polaris-bot',
              senderName: 'Polaris',
              text: '',
              card: cardPayload,
              attachments: respAttachments,
              timestamp: Date.now(),
              externalMessageId: externalId,
            };
            appended = pushAssistantMessage(finalThreadId, botCard, externalId);
          } else {
            const extracted = assistantTextTrimmed ? parsePossibleCard(assistantTextTrimmed) : null;
            if (extracted) {
              const botCard = {
                id: Date.now() + 1,
                sender: 'polaris-bot',
                senderName: 'Polaris',
                text: '',
                card: extracted,
                attachments: respAttachments,
                timestamp: Date.now(),
                externalMessageId: externalId,
              };
              appended = pushAssistantMessage(finalThreadId, botCard, externalId);
            } else {
              const botMsg = {
                id: Date.now() + 1,
                sender: 'polaris-bot',
                senderName: 'Polaris',
                text: assistantTextTrimmed,
                attachments: respAttachments,
                timestamp: Date.now(),
                externalMessageId: externalId,
              };
              appended = pushAssistantMessage(finalThreadId, botMsg, externalId);
            }
          }
          if (appended) {
            setMessagesByThread(prev => {
              const base = { ...prev };
              base[finalThreadId] = (base[finalThreadId] || []).filter(msg => msg.id !== loaderId);
              return base;
            });
          } else {
            hasImmediateAssistantContent = false;
          }
        }
        if (!hasImmediateAssistantContent) {
          setLoaderStatus(finalThreadId, loaderId, 'Still working… you’ll see a reply here once it is ready.');
          pollForAssistantReply(finalThreadId, loaderId, sendStartedAt, 240, 5000)
            .then((ok) => {
              if (ok) {
                clearLoaderTimers(loaderId);
              } else {
                setLoaderStatus(finalThreadId, loaderId, 'Still working… you’ll see a reply here once it is ready.');
              }
            })
            .catch((pollErr) => {
              console.error('Polling for delayed assistant reply failed', pollErr);
              setLoaderStatus(finalThreadId, loaderId, 'Still working… you’ll see a reply here once it is ready.');
            });
        }

        if (threadChanged) {
          setConvByThread(prev => {
            const next = { ...prev };
            if (next[targetThreadId]) {
              next[finalThreadId] = next[targetThreadId];
              delete next[targetThreadId];
            }
            return next;
          });
          setRemoteThreadByLocal(prev => {
            const next = { ...prev };
            if (next[targetThreadId]) {
              next[finalThreadId] = next[targetThreadId];
              delete next[targetThreadId];
            } else if (remoteFromData) {
              next[finalThreadId] = remoteFromData;
            }
            return next;
          });
        } else if (remoteFromData) {
          setRemoteThreadByLocal(prev => {
            if (prev[finalThreadId] === remoteFromData) return prev;
            return { ...prev, [finalThreadId]: remoteFromData };
          });
        }
      }
    } catch (e) {
      console.error('Failed to send message', e);
      const status = (e && typeof e === 'object' && typeof e.status === 'number') ? e.status : null;
      const clientError = status && status >= 400 && status < 500;
      if (!clientError) {
        pollForAssistantReply(targetThreadId, loaderId, sendStartedAt, 240, 5000)
          .then((ok) => {
            if (ok) {
              clearLoaderTimers(loaderId);
            } else {
                setLoaderStatus(targetThreadId, loaderId, 'Still working… you’ll see a reply here once it is ready.');
              console.info('Awaiting delayed assistant reply…');
            }
          })
          .catch((pollErr) => {
            console.error('Polling for delayed assistant reply failed', pollErr);
            setLoaderStatus(targetThreadId, loaderId, 'Still working… you’ll see a reply here once it is ready.');
          });
      } else {
        clearLoaderTimers(loaderId);
        setLoaderStatus(targetThreadId, loaderId, 'I had trouble with that request. Please adjust the question and try again.', false);
      }
    }
    setSending(false);
    setPendingImages([]);
  }, [
    input,
    pendingImages,
    selectedThreadId,
    meEmail,
    meName,
    setSendError,
    setMessagesByThread,
    setInput,
    getRemoteThreadId,
    updateThreadAssociations,
    pushAssistantMessage,
    pollForAssistantReply,
    setLoaderStatus,
    clearLoaderTimers,
    scheduleLoaderStatusUpdates,
    buildWidgetsFromResponse,
    parsePossibleCard,
    convByThread,
    extractRemoteThreadId,
    loadPrefs,
    savePrefs,
    setThreadNames,
    setHiddenThreads,
    setActiveThreads,
    setSelectedThreadId,
    setRemoteThreadByLocal,
    setConvByThread,
    forceScrollToBottom,
  ]);

  const handlePromptClick = useCallback((prompt) => {
    if (!prompt || !prompt.prompt) return;
    const promptText = String(prompt.prompt);
    setInput(promptText);
    setPendingImages([]);
    setTimeout(() => {
      sendMessage(promptText);
    }, 0);
  }, [sendMessage]);

  // Launch a tool via /webkit/card-action using expandActionInputs
  const openToolsMenu = (e) => setToolsMenuEl(e.currentTarget);
  const closeToolsMenu = () => setToolsMenuEl(null);
  const selectTool = (toolKey) => {
    closeToolsMenu();
    setSelectedToolKey(toolKey);
    setToolForm({});
    setToolDialogOpen(true);
  };

  const handleToolSubmit = async () => {
    const def = TOOL_DEFS[selectedToolKey];
    if (!def) { setToolDialogOpen(false); return; }
    setToolSubmitting(true);
    try {
      const toolThreadId = selectedThreadId;
      const remoteThreadIdForTool = getRemoteThreadId(toolThreadId);
      // Normalize form values to strings as expected by server
      const sanitizedForm = Object.fromEntries(
        Object.entries(toolForm).map(([k, v]) => [k, String(v ?? '').trim()])
      );
      // Ensure required fields are present; supply sensible defaults for radios
      const fields = Array.isArray(def.fields) ? def.fields : [];
      const finalForm = { ...sanitizedForm };
      const missing = [];
      const getDefault = (f) => {
        if (f.default != null) return String(f.default);
        if (f.type === 'radio') return String((f.options && f.options[0]) || '');
        return '';
      };
      fields.forEach((f) => {
        const cur = (finalForm[f.name] ?? '').trim();
        if (!cur && f.type === 'radio') {
          finalForm[f.name] = getDefault(f);
        }
        if ((f.required && !cur)) {
          // For selects/text, require non-empty
          missing.push(f.label || f.name);
        }
      });
      if (missing.length) {
        setSendError(`Please complete: ${missing.join(', ')}`);
        setToolSubmitting(false);
        return;
      }
      if (def.route === 'cardAction') {
        const localThreadId = toolThreadId || '';
        const remoteThread = typeof remoteThreadIdForTool === 'string' && remoteThreadIdForTool.trim()
          ? remoteThreadIdForTool.trim()
          : '';
        const resp = await fetch(buildApiUrl('/webkit/card-action'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-email': meEmail },
          body: JSON.stringify({
            function: def.fn,
            email: meEmail,
            display_name: meName,
            parameters: {},
            formInputs: finalForm,
            thread_id: localThreadId || undefined,
            local_thread_id: localThreadId || undefined,
            assistant_thread_id: remoteThread || undefined,
            conversation_id: convByThread[toolThreadId] || undefined,
            createTime: new Date().toISOString(),
          })
        });
        if (!resp.ok) {
          const t = await resp.text();
          throw new Error(`HTTP ${resp.status}: ${t}`);
        }
        if (false) {
          await (async () => {
            const reader = resp.body?.getReader?.();
            if (!reader) return;
            const decoder = new TextDecoder();
            let buffer = '';
            let acc = '';
            const botId = `stream_${Date.now()}_${Math.random()}`;
            // Initialize a streaming message by replacing any loader
            setMessagesByThread(prev => {
              const base = { ...prev };
              Object.keys(base).forEach(tid => { base[tid] = (base[tid] || []).filter(m => !m.loading); });
              const list = base[selectedThreadId] || [];
              base[selectedThreadId] = [...list, { id: botId, sender: 'polaris-bot', senderName: 'Polaris', text: '', timestamp: Date.now(), loading: true }];
              return base;
            });
            const updateText = (t) => setMessagesByThread(prev => {
              const base = { ...prev };
              const list = base[selectedThreadId] || [];
              base[selectedThreadId] = list.map(m => m.id === botId ? { ...m, text: t, loading: false } : m);
              return base;
            });
            const finalizeCard = (cardPayload) => setMessagesByThread(prev => {
              const base = { ...prev };
              const list = (base[selectedThreadId] || []).filter(m => m.id !== botId && !m.loading);
              base[selectedThreadId] = [...list, { id: `card_${Date.now()}`, sender: 'polaris-bot', senderName: 'Polaris', text: '', card: cardPayload, timestamp: Date.now() }];
              return base;
            });
            const safeParse = (txt) => { try { return JSON.parse(txt); } catch (_) { return null; } };
            const tryHandleJson = (line) => {
              const obj = safeParse(line);
              if (!obj) return false;
              const convId = obj.conversation_id || obj.assistant_thread_id;
              if (convId) {
                setConvByThread(prev => ({ ...prev, [selectedThreadId]: String(convId) }));
              }
              const t = obj.delta || obj.text || obj.assistant_text || '';
              if (t) { acc += String(t); updateText(acc); }
              const cardPayload = obj.cardsV2 ? { cardsV2: obj.cardsV2 } : (obj.card_json || obj.card || (obj.cards ? { cardsV2: obj.cards } : null));
              if (cardPayload) { finalizeCard(cardPayload); }
              return true;
            };
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              // Handle SSE (lines starting with data:) and NDJSON
              let idx;
              while ((idx = buffer.indexOf('\n')) !== -1) {
                const line = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 1);
                if (!line) continue;
                if (line.startsWith('data:')) {
                  const payload = line.slice(5).trim();
                  if (payload === '[DONE]') continue;
                  if (!tryHandleJson(payload)) { acc += payload; updateText(acc); }
                } else {
                  if (!tryHandleJson(line)) { acc += line; updateText(acc); }
                }
              }
            }
            // Flush remaining buffer
            const rest = buffer.trim();
            if (rest) {
              if (!tryHandleJson(rest)) { acc += rest; updateText(acc); }
            }
          })();
        } else {
          const data = await resp.json();
          const serverThreadId = data.local_thread_id || null;
          const currentThreadId = toolThreadId || selectedThreadId;
          const threadChanged = Boolean(serverThreadId && serverThreadId !== currentThreadId);
          const finalThreadId = serverThreadId || currentThreadId;
          if (!finalThreadId) {
            throw new Error('No thread identifier available for tool response.');
          }

          if (threadChanged && currentThreadId) {
            setMessagesByThread(prev => {
              const base = { ...prev };
              const messages = base[currentThreadId] || [];
              base[finalThreadId] = messages;
              delete base[currentThreadId];
              return base;
            });
            setThreadNames(prev => {
              const next = { ...prev };
              if (next[currentThreadId] && !next[finalThreadId]) {
                next[finalThreadId] = next[currentThreadId];
              }
              delete next[currentThreadId];
              const { hidden } = loadPrefs();
              savePrefs(next, hidden);
              return next;
            });
            setHiddenThreads(prev => {
              if (!prev.has(currentThreadId)) return prev;
              const next = new Set(prev);
              next.delete(currentThreadId);
              next.add(finalThreadId);
              const { names } = loadPrefs();
              savePrefs(names, Array.from(next));
              return next;
            });
            setActiveThreads(prev => {
              const filtered = prev.filter(id => id !== currentThreadId);
              if (!filtered.includes(finalThreadId)) {
                filtered.unshift(finalThreadId);
              }
              return filtered;
            });
          } else {
            setActiveThreads(prev => {
              if (prev.includes(finalThreadId)) return prev;
              return [finalThreadId, ...prev];
            });
          }

          setSelectedThreadId(finalThreadId);
          updateThreadAssociations(finalThreadId, data);

          const respAttachments = Array.isArray(data.attachment_urls) ? data.attachment_urls.filter(Boolean).map(u => ({ url: String(u) })) : [];
          const widgets = buildWidgetsFromResponse(selectedToolKey, data, respAttachments);
          const externalId = data.assistant_message_id || data.response_id || null;
          if (widgets.length) {
            const botMsg = {
              id: Date.now() + Math.random(),
              sender: 'polaris-bot',
              senderName: 'Polaris',
              text: data.assistant_text || '',
              widgets,
              attachments: respAttachments,
              timestamp: Date.now(),
              externalMessageId: externalId,
            };
            pushAssistantMessage(finalThreadId, botMsg, externalId);
          } else if (data.cardsV2) {
            const botCard = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: '', card: { cardsV2: data.cardsV2 }, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
            pushAssistantMessage(finalThreadId, botCard, externalId);
          } else if (data.assistant_text) {
            const botMsg = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: data.assistant_text, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
            pushAssistantMessage(finalThreadId, botMsg, externalId);
          }
        }
      } else {
        // Unified handler via /webkit/messages
        const message = typeof def.buildMessage === 'function' ? def.buildMessage(toolForm) : '';
        const localThreadId = toolThreadId || '';
        const remoteThread = typeof remoteThreadIdForTool === 'string' && remoteThreadIdForTool.trim()
          ? remoteThreadIdForTool.trim()
          : '';
        const resp = await fetch(buildApiUrl('/webkit/messages'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-user-email': meEmail },
          body: JSON.stringify({
            email: meEmail,
            display_name: meName,
            thread_id: localThreadId || undefined,
            local_thread_id: localThreadId || undefined,
            assistant_thread_id: remoteThread || undefined,
            conversation_id: convByThread[toolThreadId] || undefined,
            message,
            createTime: new Date().toISOString(),
          })
        });
        if (!resp.ok) {
          const t = await resp.text();
          throw new Error(`HTTP ${resp.status}: ${t}`);
        }
        if (false) {
          await (async () => {
            const reader = resp.body?.getReader?.();
            if (!reader) return;
            const decoder = new TextDecoder();
            let buffer = '';
            let acc = '';
            const botId = `stream_${Date.now()}_${Math.random()}`;
            setMessagesByThread(prev => {
              const base = { ...prev };
              Object.keys(base).forEach(tid => { base[tid] = (base[tid] || []).filter(m => !m.loading); });
              const list = base[selectedThreadId] || [];
              base[selectedThreadId] = [...list, { id: botId, sender: 'polaris-bot', senderName: 'Polaris', text: '', timestamp: Date.now(), loading: true }];
              return base;
            });
            const updateText = (t) => setMessagesByThread(prev => {
              const base = { ...prev };
              const list = base[selectedThreadId] || [];
              base[selectedThreadId] = list.map(m => m.id === botId ? { ...m, text: t, loading: false } : m);
              return base;
            });
            const finalizeCard = (cardPayload) => setMessagesByThread(prev => {
              const base = { ...prev };
              const list = (base[selectedThreadId] || []).filter(m => m.id !== botId && !m.loading);
              base[selectedThreadId] = [...list, { id: `card_${Date.now()}`, sender: 'polaris-bot', senderName: 'Polaris', text: '', card: cardPayload, timestamp: Date.now() }];
              return base;
            });
            const safeParse = (txt) => { try { return JSON.parse(txt); } catch (_) { return null; } };
            const tryHandleJson = (line) => {
              const obj = safeParse(line);
              if (!obj) return false;
              const convId = obj.conversation_id || obj.assistant_thread_id;
              if (convId) {
                setConvByThread(prev => ({ ...prev, [selectedThreadId]: String(convId) }));
              }
              const t = obj.delta || obj.text || obj.assistant_text || '';
              if (t) { acc += String(t); updateText(acc); }
              const cardPayload = obj.cardsV2 ? { cardsV2: obj.cardsV2 } : (obj.card_json || obj.card || (obj.cards ? { cardsV2: obj.cards } : null));
              if (cardPayload) { finalizeCard(cardPayload); }
              return true;
            };
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              let idx;
              while ((idx = buffer.indexOf('\n')) !== -1) {
                const line = buffer.slice(0, idx).trim();
                buffer = buffer.slice(idx + 1);
                if (!line) continue;
                if (line.startsWith('data:')) {
                  const payload = line.slice(5).trim();
                  if (payload === '[DONE]') continue;
                  if (!tryHandleJson(payload)) { acc += payload; updateText(acc); }
                } else {
                  if (!tryHandleJson(line)) { acc += line; updateText(acc); }
                }
              }
            }
            const rest = buffer.trim();
            if (rest) {
              if (!tryHandleJson(rest)) { acc += rest; updateText(acc); }
            }
          })();
        } else {
          const data = await resp.json();
          const localThread = data.local_thread_id || toolThreadId;
          if (localThread) setActiveThreads([localThread]);
          updateThreadAssociations(localThread, data);
          const respAttachments = Array.isArray(data.attachment_urls) ? data.attachment_urls.filter(Boolean).map(u => ({ url: String(u) })) : [];
          const externalId = data.assistant_message_id || data.response_id || null;
          if (data.cardsV2) {
            const botCard = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: '', card: { cardsV2: data.cardsV2 }, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
            pushAssistantMessage(localThread, botCard, externalId);
          } else if (data.assistant_text) {
            const botMsg = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: data.assistant_text, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
            pushAssistantMessage(localThread, botMsg, externalId);
          }
        }
      }
      setToolDialogOpen(false);
    } catch (e) {
      setSendError(formatSendError(e));
    } finally {
      setToolSubmitting(false);
    }
  };

  const renderField = (f) => {
    const val = toolForm[f.name] ?? (f.default ?? (f.type === 'radio' ? (f.options?.[0] ?? '') : ''));
    const setVal = (v) => setToolForm(prev => ({ ...prev, [f.name]: v }));
    const commonSx = { my: 0.75 };
    if (f.type === 'text') return (<TextField key={f.name} label={f.label} value={val} onChange={e=>setVal(e.target.value)} fullWidth size="small" sx={commonSx} />);
    if (f.type === 'textarea') return (<TextField key={f.name} label={f.label} value={val} onChange={e=>setVal(e.target.value)} fullWidth size="small" sx={commonSx} multiline minRows={3} />);
    if (f.type === 'select') return (
      <MuiFormControl key={f.name} fullWidth size="small" sx={commonSx}>
        <InputLabel>{f.label}</InputLabel>
        <Select label={f.label} value={val} onChange={e=>setVal(e.target.value)}>
          {(f.options||[]).map(opt => (<MenuItem key={opt} value={opt}>{opt}</MenuItem>))}
        </Select>
      </MuiFormControl>
    );
    if (f.type === 'radio') return (
      <Box key={f.name} sx={{ ...commonSx }}>
        <FormLabel component="legend">{f.label}</FormLabel>
        <RadioGroup row value={val} onChange={e=>setVal(e.target.value)}>
          {(f.options||[]).map(opt => (<FormControlLabel key={opt} value={opt} control={<Radio size="small" />} label={opt} />))}
        </RadioGroup>
      </Box>
    );
    if (f.type === 'datetime') return (
      <TextField key={f.name} label={f.label} type="datetime-local" value={val} onChange={e=>setVal(e.target.value)} size="small" sx={commonSx} InputLabelProps={{ shrink: true }} />
    );
    if (f.type === 'date') return (
      <TextField key={f.name} label={f.label} type="date" value={val} onChange={e=>setVal(e.target.value)} size="small" sx={commonSx} InputLabelProps={{ shrink: true }} />
    );
    if (f.type === 'time') return (
      <TextField key={f.name} label={f.label} type="time" value={val} onChange={e=>setVal(e.target.value)} size="small" sx={commonSx} InputLabelProps={{ shrink: true }} />
    );
    return null;
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const processAttachments = async (files) => {
    try {
      if (!attachmentsEnabled) {
        setSendError(isMobile ? 'Image uploads are not available on mobile devices.' : 'Attachments are disabled in your appearance settings.');
        return false;
      }
      const queue = Array.from(files || []);
      if (!queue.length) return false;
      const availableSlots = (attachmentsConfig.maxCount ?? 6) - pendingImages.length;
      if (availableSlots <= 0) {
        setSendError('Attachment limit reached. Remove an attachment to add a new one.');
        return false;
      }
      const maxSizeBytes = attachmentsConfig.maxSize ?? 10 * 1024 * 1024;
      const accepted = [];
      const rejected = [];
      for (const file of queue) {
        if (accepted.length >= availableSlots) {
          rejected.push(file.name);
          continue;
        }
        if (file.size > maxSizeBytes) {
          rejected.push(file.name);
          continue;
        }
        accepted.push(file);
      }
      if (rejected.length) {
        setSendError(`Skipped ${rejected.length} file(s) that exceeded your attachment limits.`);
      }
      if (!accepted.length) {
        return false;
      }
      const dataUrls = await Promise.all(accepted.map(readFileAsDataUrl));
      const newItems = accepted.map((f, i) => ({ name: f.name, type: f.type, size: f.size, dataUrl: dataUrls[i] }));
      setPendingImages(prev => [...prev, ...newItems]);
      return true;
    } catch (err) {
      console.error('Failed to read attachment:', err);
      setSendError('Unable to read one or more files. Please try again.');
      return false;
    }
  };

  const handleFileInputChange = async (e) => {
    await processAttachments(e?.target?.files);
    if (e?.target) e.target.value = '';
  };

  const handleComposerPaste = async (event) => {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;
    const files = Array.from(clipboardData.files || []);
    const items = Array.from(clipboardData.items || []);
    const imageFiles = [
      ...files.filter((file) => file && file.type && file.type.startsWith('image/')),
      ...items
        .filter((item) => item.kind === 'file' && item.type && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter(Boolean),
    ];
    if (!imageFiles.length) return;
    const pastedText = clipboardData.getData('text') || '';
    if (attachmentsEnabled) {
      event.preventDefault();
      if (pastedText) {
        setInput((prev) => `${prev}${pastedText}`);
      }
      await processAttachments(imageFiles);
    } else {
      setSendError(
        isMobile
          ? 'Image uploads are not available on mobile devices.'
          : 'Attachments are disabled in your appearance settings.'
      );
    }
  };

  const removePendingImage = (idx) => {
    setPendingImages(prev => prev.filter((_, i) => i !== idx));
  };

  const initialThreadReady = selectedThreadId && messagesByThread[selectedThreadId] !== undefined;
  const appBooting = loading || creatingThread || !initialThreadReady;

  if (appBooting) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: startAccentColor }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap', minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="img" src={POLARIS_ICON_URL} alt="Polaris" sx={{ height: 32, width: 32 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, display: { xs: 'block', sm: 'none' }, color: accentColor }}>
              {selectedThreadId ? (threadNames[selectedThreadId] || 'Conversation') : 'Conversation'}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          {/* Desktop thread picker and actions */}
          {THREAD_CONTROLS_ENABLED && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.25 }}>
              <MuiFormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="thread-select-label">Conversation</InputLabel>
                <Select
                  labelId="thread-select-label"
                  label="Conversation"
                  value={selectedThreadId || ''}
                  onChange={e => setSelectedThreadId(e.target.value)}
                >
                  {(activeThreads || []).map((tid, i) => (
                    <MenuItem key={tid} value={tid}>{threadNames[tid] || `Chat ${i + 1}`}</MenuItem>
                  ))}
                </Select>
              </MuiFormControl>
              <Tooltip title="Thread actions">
                <span>
                  <IconButton aria-label="thread actions" size="small" onClick={(e) => setActionsMenuEl(e.currentTarget)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu anchorEl={actionsMenuEl} open={Boolean(actionsMenuEl)} onClose={() => setActionsMenuEl(null)}>
                <MenuItem onClick={() => {
                  setActionsMenuEl(null);
                  if (!selectedThreadId) return;
                  setRenameTargetId(selectedThreadId);
                  setRenameDraft(threadNames[selectedThreadId] || displayNameForThread(selectedThreadId, Math.max(0, activeThreads.indexOf(selectedThreadId))));
                  setRenameOpen(true);
                }} disabled={!selectedThreadId}>
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Rename conversation" />
                </MenuItem>
                <MenuItem onClick={() => { setActionsMenuEl(null); setNewDraft(''); setNewOpen(true); }}>
                  <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="New conversation" />
                </MenuItem>
                <MenuItem onClick={() => { setActionsMenuEl(null); if (!selectedThreadId) return; setDeleteTargetId(selectedThreadId); setDeleteOpen(true); }} disabled={!selectedThreadId}>
                  <ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Delete conversation" />
                </MenuItem>
              </Menu>
            </Box>
          )}
          {/* Mobile actions menu */}
          {THREAD_CONTROLS_ENABLED && (
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1 }}>
              <Tooltip title="Conversation menu">
                <IconButton onClick={(e) => setThreadMenuEl(e.currentTarget)} size="small">
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={threadMenuEl} open={Boolean(threadMenuEl)} onClose={() => setThreadMenuEl(null)}>
                {(activeThreads || []).map((tid, i) => (
                  <MenuItem key={tid} onClick={() => { setSelectedThreadId(tid); setThreadMenuEl(null); }}>
                    <ListItemText primary={threadNames[tid] || `Chat ${i + 1}`} secondary={tid} />
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem onClick={() => { setThreadMenuEl(null); setNewDraft(''); setNewOpen(true); }}>
                  <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="New conversation" />
                </MenuItem>
                <MenuItem disabled={!selectedThreadId} onClick={() => {
                  setThreadMenuEl(null);
                  if (!selectedThreadId) return;
                  setRenameTargetId(selectedThreadId);
                  setRenameDraft(threadNames[selectedThreadId] || displayNameForThread(selectedThreadId, Math.max(0, activeThreads.indexOf(selectedThreadId))));
                  setRenameOpen(true);
                }}>
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Rename current" />
                </MenuItem>
                <MenuItem disabled={!selectedThreadId} onClick={() => { setThreadMenuEl(null); if (!selectedThreadId) return; setDeleteTargetId(selectedThreadId); setDeleteOpen(true); }}>
                  <ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Delete current" />
              </MenuItem>
            </Menu>
          </Box>
          )}
          <Tooltip title="Customize appearance">
            <IconButton size="small" onClick={() => setSettingsOpen(true)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{user.email}</Typography>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Box ref={listRef} sx={{ position: 'relative', flex: 1, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
        {currentMessages.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, height: '100%', textAlign: 'center' }}>
            <Typography variant="h5" fontWeight={600} sx={{ color: startAccentColor }}>
              {startGreeting}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              I'll be your north star for all things insurance.
            </Typography>
            {startPrompts.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                {startPrompts.map((prompt, idx) => (
                  <Chip
                    key={`${prompt.prompt}-${idx}`}
                    label={prompt.name || prompt.prompt}
                    onClick={() => handlePromptClick(prompt)}
                    sx={{
                      backgroundColor: startAccentColor,
                      color: promptTextColor,
                      fontWeight: 600,
                      textAlign: 'center',
                      px: 1.5,
                      py: 0.75,
                      maxWidth: { xs: '100%', sm: 280 },
                      height: 'auto',
                      lineHeight: 1.25,
                      overflowWrap: { xs: 'break-word', sm: 'normal' },
                      wordBreak: { xs: 'break-word', sm: 'normal' },
                      alignItems: 'center',
                      '& .MuiChip-label': {
                        display: 'block',
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        overflow: { xs: 'visible', sm: 'hidden' },
                        textOverflow: { xs: 'clip', sm: 'ellipsis' },
                        padding: 0,
                        lineHeight: 1.25,
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          currentMessages.map(msg => (
            <ChatMessage
              key={msg.id}
              message={msg}
              meEmail={meEmail}
              meName={meName}
              threadId={selectedThreadId}
              remoteThreadId={remoteThreadIdForSelected}
              userAccentColor={startAccentColor}
              onAction={(data) => {
                const localThread = data.local_thread_id || selectedThreadId;
                if (localThread) setActiveThreads([localThread]);
                updateThreadAssociations(localThread, data);
                const respAttachments = Array.isArray(data.attachment_urls) ? data.attachment_urls.filter(Boolean).map(u => ({ url: String(u) })) : [];
                const externalId = data.assistant_message_id || data.response_id || null;
                const widgets = buildWidgetsFromResponse(data._toolKey || null, data, respAttachments);
                if (widgets.length) {
                  const botMsg = {
                    id: Date.now() + Math.random(),
                    sender: 'polaris-bot',
                    senderName: 'Polaris',
                    text: data.assistant_text || '',
                    widgets,
                    attachments: respAttachments,
                    timestamp: Date.now(),
                    externalMessageId: externalId,
                  };
                  pushAssistantMessage(localThread, botMsg, externalId);
                } else if (data.cardsV2) {
                  const botCard = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: '', card: { cardsV2: data.cardsV2 }, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
                  pushAssistantMessage(localThread, botCard, externalId);
                } else if (data.assistant_text) {
                  const botMsg = { id: Date.now() + Math.random(), sender: 'polaris-bot', senderName: 'Polaris', text: data.assistant_text, attachments: respAttachments, timestamp: Date.now(), externalMessageId: externalId };
                  pushAssistantMessage(localThread, botMsg, externalId);
                }
              }}
            />
          ))
        )}
      </Box>

      <Box component={Paper} elevation={3} square sx={{ p: { xs: 1, sm: 1.5 }, borderTopLeftRadius: 12, borderTopRightRadius: 12, position: 'sticky', bottom: 0 }}>
        {sendError && (
          <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
            {sendError}
          </Typography>
        )}
        {pendingImages.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {pendingImages.map((att, idx) => (
              <Box key={idx} sx={{ position: 'relative' }}>
                <Box component="img" src={att.dataUrl} alt={att.name} sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid #e0e0e0' }} />
                <IconButton size="small" onClick={() => removePendingImage(idx)} sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'background.paper' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleFileInputChange} />
          {attachmentsEnabled && (
            <Tooltip title="Attach images">
              <span>
                <IconButton onClick={() => fileInputRef.current?.click()} disabled={sending || !selectedThreadId}>
                  <PhotoCameraIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Open tools">
            <span>
              <IconButton onClick={openToolsMenu} disabled={sending || !selectedThreadId}>
                <ExtensionIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Menu anchorEl={toolsMenuEl} open={Boolean(toolsMenuEl)} onClose={closeToolsMenu}>
            {TOOL_OPTIONS.map(t => (
              <MenuItem key={t.key} onClick={() => selectTool(t.key)} disabled={!selectedThreadId}>
                <ListItemText primary={t.label} secondary={t.description || ''} />
              </MenuItem>
            ))}
          </Menu>
          <TextField
            fullWidth
            placeholder={composerPlaceholder}
            value={input}
            onChange={e => setInput(e.target.value)}
            onPaste={handleComposerPaste}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            disabled={sending || !selectedThreadId}
            size={isMobile ? 'small' : composerSize}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => sendMessage()}
            disabled={sending || !selectedThreadId}
            size={isMobile ? 'small' : composerSize}
            sx={{
              minWidth: isMobile ? 40 : 48,
              px: isMobile ? 1 : 1.25,
            }}
          >
            {sending ? <CircularProgress size={16} sx={{ color: 'primary.contrastText' }} /> : <SendIcon />}
          </Button>
        </Box>
      </Box>
      {/* Tool dialog */}
      <Dialog open={toolDialogOpen} onClose={() => setToolDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{TOOL_DEFS[selectedToolKey]?.title || 'Tool'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {(TOOL_DEFS[selectedToolKey]?.fields || []).map(renderField)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleToolSubmit} variant="contained" disabled={toolSubmitting || !selectedThreadId}>{toolSubmitting ? 'Submitting…' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>

      {/* Rename conversation dialog */}
      {THREAD_CONTROLS_ENABLED && (
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rename Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Conversation name"
            fullWidth
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const val = String(renameDraft || '').trim();
              if (val && renameTargetId) renameThread(renameTargetId, val);
              setRenameOpen(false);
            }}
            disabled={!renameDraft || !renameTargetId}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* New conversation dialog */}
      {THREAD_CONTROLS_ENABLED && (
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Conversation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Optional name"
            fullWidth
            placeholder="e.g., Client follow-up"
            value={newDraft}
            onChange={(e) => setNewDraft(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={newCreating}>Cancel</Button>
          <Button
            variant="contained"
            disabled={newCreating}
            onClick={async () => {
              try {
                setNewCreating(true);
                const nameDraft = String(newDraft || '').trim();
                await createNewThread(nameDraft);
                setNewOpen(false);
              }
              finally { setNewCreating(false); }
            }}
          >
            {newCreating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Delete confirmation dialog */}
      {THREAD_CONTROLS_ENABLED && (
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`Are you sure you want to delete "${deleteTargetId ? (threadNames[deleteTargetId] || displayNameForThread(deleteTargetId, Math.max(0, activeThreads.indexOf(deleteTargetId)))) : ''}"? This will remove it locally and mark it inactive on the server.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            const id = deleteTargetId;
            setDeleteOpen(false);
            if (!id) return;
            hideThread(id);
            setMessagesByThread(prev => { const next = { ...prev }; delete next[id]; return next; });
            setActiveThreads(prev => prev.filter(t => t !== id));
            if (selectedThreadId === id) {
              const remaining = activeThreads.filter(t => t !== id);
              setSelectedThreadId(remaining[0] || '');
            }
            try {
              const resp = await fetch(buildApiUrl('/webkit/delete-thread'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email: meEmail,
                  thread_id: id,
                  local_thread_id: id,
                })
              });
              if (resp.ok) {
                // Remove any stored conversation id for this thread locally
                setConvByThread(prev => {
                  const next = { ...prev };
                  try { delete next[id]; } catch (_) {}
                  return next;
                });
                setRemoteThreadByLocal(prev => {
                  if (!prev[id]) return prev;
                  const next = { ...prev };
                  try { delete next[id]; } catch (_) {}
                  return next;
                });
              } else { console.warn('Server delete-thread returned non-OK:', resp.status); }
            } catch (e) { console.warn('Failed to sync delete-thread to server:', e); }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
      )}

      <Drawer anchor="right" open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <Box sx={{ width: { xs: 300, sm: 360 }, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" fontWeight={700}>Chat appearance</Typography>
          <Typography variant="body2" color="text.secondary">
            Customize the interface so it matches your style.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="chatkit-color-scheme">Color scheme</InputLabel>
            <Select
              labelId="chatkit-color-scheme"
              label="Color scheme"
              value={colorScheme}
              onChange={(event) => updateChatkitOptions(prev => ({
                ...prev,
                theme: { ...prev.theme, colorScheme: event.target.value },
              }))}
            >
              <MenuItem value="light">Light</MenuItem>
              <MenuItem value="dark">Dark</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Accent color"
            type="color"
            size="small"
            value={accentColor}
            onChange={(event) => updateChatkitOptions(prev => ({
              ...prev,
              theme: {
                ...prev.theme,
                color: {
                  ...(prev.theme?.color || {}),
                  accent: {
                    ...(prev.theme?.color?.accent || {}),
                    primary: event.target.value,
                  },
                },
              },
            }))}
            InputLabelProps={{ shrink: true }}
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>Corner style</Typography>
            <ToggleButtonGroup
              value={chatkitOptions?.theme?.radius || 'soft'}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  updateChatkitOptions(prev => ({
                    ...prev,
                    theme: { ...prev.theme, radius: value },
                  }));
                }
              }}
              size="small"
            >
              <ToggleButton value="sharp">Sharp</ToggleButton>
              <ToggleButton value="soft">Soft</ToggleButton>
              <ToggleButton value="round">Round</ToggleButton>
              <ToggleButton value="pill">Pill</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom>Layout density</Typography>
            <ToggleButtonGroup
              value={chatkitOptions?.theme?.density || 'normal'}
              exclusive
              onChange={(_, value) => {
                if (value) {
                  updateChatkitOptions(prev => ({
                    ...prev,
                    theme: { ...prev.theme, density: value },
                  }));
                }
              }}
              size="small"
            >
              <ToggleButton value="compact">Compact</ToggleButton>
              <ToggleButton value="normal">Comfortable</ToggleButton>
              <ToggleButton value="spacious">Spacious</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Divider flexItem />
          <Typography variant="subtitle2">Attachments</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={attachmentsEnabled && !isMobile}
                onChange={(event) => updateChatkitOptions(prev => ({
                  ...prev,
                  composer: {
                    ...prev.composer,
                    attachments: {
                      ...(prev.composer?.attachments || {}),
                      enabled: event.target.checked,
                    },
                  },
                }))}
                disabled={isMobile}
              />
            )}
            label={isMobile ? 'Image uploads disabled on mobile' : 'Enable image uploads'}
          />
          {isMobile && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Uploading images is currently unavailable on mobile devices.
            </Typography>
          )}
          <Box sx={{ px: 1, opacity: attachmentsEnabled && !isMobile ? 1 : 0.5 }}>
            <Typography variant="caption" color="text.secondary">Maximum attachments per message</Typography>
            <Slider
              value={attachmentsConfig.maxCount ?? 6}
              min={1}
              max={10}
              step={1}
              onChange={(_, value) => {
                const count = Array.isArray(value) ? value[0] : value;
                updateChatkitOptions(prev => ({
                  ...prev,
                  composer: {
                    ...prev.composer,
                    attachments: {
                      ...(prev.composer?.attachments || {}),
                      maxCount: count,
                    },
                  },
                }));
              }}
              disabled={!attachmentsEnabled || isMobile}
            />
            <TextField
              label="Max file size (MB)"
              type="number"
              size="small"
              inputProps={{ min: 1 }}
              value={Math.round((attachmentsConfig.maxSize ?? (10 * 1024 * 1024)) / (1024 * 1024))}
              onChange={(event) => {
                const mb = Math.max(1, Number(event.target.value) || 1);
                updateChatkitOptions(prev => ({
                  ...prev,
                  composer: {
                    ...prev.composer,
                    attachments: {
                      ...(prev.composer?.attachments || {}),
                      maxSize: mb * 1024 * 1024,
                    },
                  },
                }));
              }}
              disabled={!attachmentsEnabled || isMobile}
            />
          </Box>
          <Divider flexItem />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', mt: 1 }}>
            <Button color="inherit" onClick={() => { resetChatkitOptions(); }}>
              Reset to defaults
            </Button>
            <Button variant="contained" onClick={() => setSettingsOpen(false)}>Close</Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
const radiusPreset = {
  pill: 999,
  round: 24,
  soft: 16,
  sharp: 6,
};

const densityPadding = {
  compact: 6,
  normal: 10,
  spacious: 14,
};

const buildThemeFromOptions = (options) => {
  const mode = options?.theme?.colorScheme === 'dark' ? 'dark' : 'light';
  const accent = options?.theme?.color?.accent?.primary || '#6b3acc';
  const radius = radiusPreset[options?.theme?.radius || 'soft'] ?? 16;
  const density = options?.theme?.density || 'normal';
  const fontFamily = defaultChatKitOptions.theme.typography.fontFamily;
  const buttonPadding = densityPadding[density] ?? densityPadding.normal;
  const backgroundDefault = mode === 'dark' ? '#0f1116' : '#f4f6fb';
  const backgroundPaper = mode === 'dark' ? '#161b22' : '#ffffff';
  const divider = mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(17,16,18,0.08)';

  return createTheme({
    palette: {
      mode,
      primary: { main: accent },
      secondary: { main: '#7c4dff' },
      background: { default: backgroundDefault, paper: backgroundPaper },
      text: mode === 'dark'
        ? { primary: '#f4f5f7', secondary: '#b5bac8' }
        : { primary: '#111012', secondary: '#5a6474' },
      divider,
    },
    shape: { borderRadius: radius },
    typography: { fontFamily },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            boxShadow: mode === 'dark'
              ? '0 18px 40px rgba(0,0,0,0.45)'
              : '0 12px 24px rgba(17,16,18,0.08)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            padding: `${buttonPadding}px ${buttonPadding + 8}px`,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': { borderRadius: radius },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: Math.max(radius - 4, 4),
          },
        },
      },
    },
  });
};

const POLARIS_FAVICON_URL = POLARIS_ICON_URL;

const PolarisBranding = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const previousTitle = document.title;
    document.title = 'Polaris';

    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/png';
    faviconLink.href = POLARIS_FAVICON_URL;
    faviconLink.dataset.polarisFavicon = 'true';
    document.head.appendChild(faviconLink);

    return () => {
      document.title = previousTitle;
      if (faviconLink.parentNode) {
        faviconLink.parentNode.removeChild(faviconLink);
      }
    };
  }, []);

  return null;
};

const AppShell = () => {
  const { chatkitOptions } = useChatKitTheme();
  const theme = useMemo(() => buildThemeFromOptions(chatkitOptions), [chatkitOptions]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PolarisBranding />
      <PolarisChat />
    </ThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <ChatKitThemeProvider>
        <AppShell />
      </ChatKitThemeProvider>
    </AuthProvider>
  );
}

export default App;
